import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { capsuleConfigAPI, tenantsAPI, skillsAPI, capsulesAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tag, AlertTriangle, Save, Loader2, Plus, Trash2,
  RotateCcw, HelpCircle, Zap, ChevronDown, ChevronUp, Info, KeyRound, FlaskConical, Layers,
  Sparkles, FileText, Wrench, Database, Clock,
  Bell,
  Compass,
  Sliders,
  Eye,
} from 'lucide-react';
import CapsuleWorkflowTab from '@/components/CapsuleWorkflowTab';
import { toast } from 'sonner';
import AvisosDeLaCapsula from '@/components/AvisosDeLaCapsula';
import LaConformidad from '@/components/LaConformidad';
import Criterios from '@/components/Criterios';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';
import SelectorDePersona from '@/components/SelectorDePersona';
import ParametrosDeCapacidad from '@/components/ParametrosDeCapacidad';
import PantallasPorRol from '@/components/PantallasPorRol';
import CapsuleConfigHelp, { CapsuleConfigHelpLink } from '@/components/CapsuleConfigHelp';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import {
  GUIDANCE_FIELD_HELP,
  GUIDANCE_TAB_INTRO,
  COORDINACION_INLINE_EXTRA,
  SKILLS_TAB_INTRO,
  LLM_TAB_INTRO,
} from '@/lib/capsuleConfigHelpContent';
import PurgeCapsuleDialog from '@/components/PurgeCapsuleDialog';
import CapsuleKBAccessPanel from '@/components/CapsuleKBAccessPanel';
import CapsuleApiTokensPanel from '@/components/CapsuleApiTokensPanel';

const CAPSULE_CFG_LS = 'omnisys_capsule_config_capsule_id';
// Si una pestaña no figura aca, `activeTab` no la reconoce y cae en 'coordinacion': el clic
// parece no hacer nada. Paso al agregar 'api' el 2026-08-19.
/**
 * Las banderas de un estado, con el nombre corto que se ve y la explicación que se lee al pasar
 * por encima.
 *
 * **Son banderas y no nombres**: una cápsula de mantenimiento llama `finalizado` a lo que sistemas
 * llama `resuelto`, y las dos tienen que poder declarar lo mismo sin tocar código. El nombre corto
 * entra en el ancho de una columna; la explicación es lo que dice para qué sirve.
 */
/**
 * Las banderas de un estado: **qué es, para qué sirve, a qué afecta, y un ejemplo.**
 *
 * Cuatro campos y no uno, porque un nombre corto no alcanza para decidir. Diego lo dijo así el
 * 2026-08-26: *"se agregaron muchísimas parametrizaciones y no tengo idea cómo afectan"*. Prender
 * algo sin saber el efecto es más peligroso que no prenderlo.
 *
 * **Son banderas y no nombres**: una cápsula de mantenimiento llama `finalizado` a lo que sistemas
 * llama `resuelto`, y las dos declaran lo mismo sin tocar código.
 */
const BANDERAS_DEL_ESTADO = [
  ['es_inicial', 'ini',
   'Con cuál nace un caso.',
   'Afecta: todo caso nuevo arranca acá, venga del chat, del mail o de una persona. Tiene que haber exactamente uno por cápsula — si hay dos o ninguno, el guardado te lo rechaza.'],
  ['cuenta_como_abierto', 'abierto',
   'Si el caso sigue en juego.',
   'Afecta: las bandejas, los tableros y el contador de «en curso». Ejemplo: «en espera del usuario» normalmente sigue abierto; «anulado» no.'],
  ['detiene_el_reloj', 'detiene',
   'Si al entrar deja de correr el tiempo de resolución.',
   'Afecta: el semáforo del compromiso y el cumplimiento del SLA. Varios estados pueden tenerla: «resuelto» y «anulado» terminan el caso por motivos distintos y los dos paran el reloj. Si el caso se reabre, el reloj se reanuda y queda registrada la reapertura.'],
  ['implica_atendido', 'atendido',
   'Si entrar cuenta como que alguien se hizo cargo.',
   'Afecta: la medición de «cuánto tardamos en atender», distinta de «cuánto tardamos en resolver». Ejemplo: pasar a «en curso» suele implicar atención; «nuevo» no.'],
  ['excluye_del_sla', 'sin SLA',
   'Que el caso no cuente en las estadísticas de cumplimiento.',
   'Afecta: los porcentajes del tablero. Ejemplo: un caso «anulado» o «duplicado» no debería contar ni como cumplido ni como incumplido — si contara, ensucia el número hacia cualquiera de los dos lados.'],
  ['exige_predecesores_cumplidos', 'predec',
   'Si al entrar hay que avisar de las dependencias sin terminar.',
   'Afecta: sólo avisa, no bloquea. Ejemplo: pasar a «en curso» un caso que depende de otro que no terminó muestra la advertencia, y la persona decide.'],
  ['exige_resolucion', 'resol',
   'Si al cerrar hay que escribir qué se hizo.',
   'Afecta: al cerrar desde la pantalla se abre un diálogo pidiendo la resolución, y sin texto no cierra. **Los agentes quedan exentos** — si cierra un agente, el campo queda vacío y se ve vacío. Lo que escribís alimenta la memoria del área: es lo que hace que el próximo con el mismo problema vea «esto ya pasó, se resolvió así». Sin esto, un modelo intenta adivinarlo leyendo la conversación. Ejemplo: prendela en «resuelto» y **no** en «anulado» — sólo uno de los dos deja conocimiento.'],
  ['exige_conformidad', 'conform',
   'Si para entrar hace falta que quien pidió el caso haya calificado.',
   'Afecta: a este estado **no se llega a mano**. Lo llega la calificación del solicitante, y si no califica, el cierre automático —que se configura en esta misma pestaña, en «Conformidad»— lo mueve a los X días sin nota. A mano te lo rechaza con el motivo. Prendela en un solo estado: es el final del circuito, no un paso más. Ejemplo: «cerrado» sí, «resuelto» no — resuelto es cuando el trabajo terminó, cerrado es cuando el que pidió dijo cómo le fue.'],
];

const CONFIG_TABS = ['coordinacion', 'capacidades', 'criterio', 'pantallas', 'guias', 'trabajo', 'avisos', 'skills', 'labels', 'intents', 'llm', 'kb', 'api'];

// ── Inline contextual help panel ──
function InlineHelp({ children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-4">
      <button onClick={() => setOpen(!open)} data-testid="inline-help-toggle"
        className="flex items-center gap-1.5 text-[11px] text-brand-primary hover:text-[#001F6D] transition-colors">
        <Info className="w-3.5 h-3.5" />
        <span className="font-medium">{open ? 'Ocultar ayuda' : 'Como funciona esta seccion?'}</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-2 p-4 bg-blue-50/60 border border-blue-200/60 rounded-sm text-xs text-zinc-700 leading-relaxed space-y-2 animate-in slide-in-from-top-1 duration-200">
          {children}
        </div>
      )}
    </div>
  );
}

function CapsuleAIGenerateButton({ contentType, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState('');
  const [generating, setGenerating] = useState(false);
  const handleGenerate = async () => {
    if (!desc.trim()) { toast.error('Escribe una descripcion breve'); return; }
    setGenerating(true);
    try {
      const res = await skillsAPI.generateContent({
        content_type: contentType,
        description: desc.trim(),
      });
      onGenerated(res.data.content);
      toast.success('Contenido generado con IA');
      setOpen(false);
      setDesc('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando contenido');
    } finally {
      setGenerating(false);
    }
  };
  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}
        data-testid={`capsule-ai-${contentType}`}
        title="Genera borrador con IA; revisá IDs de agentes y probá en Test Coordinador"
        className="h-7 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 gap-1">
        <Sparkles className="w-3 h-3" /> IA
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap justify-end">
      <Input
        data-testid={`capsule-ai-desc-${contentType}`}
        value={desc}
        onChange={e => setDesc(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleGenerate(); if (e.key === 'Escape') setOpen(false); }}
        placeholder="Describe que criterios necesitas..."
        className="h-7 text-xs w-56 rounded-sm"
        autoFocus
      />
      <Button type="button" size="sm" className="h-7 text-xs" disabled={generating} onClick={handleGenerate}>
        {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Generar'}
      </Button>
      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setOpen(false)}>Cancelar</Button>
    </div>
  );
}

function GuidanceFieldHelpPanel({ fieldKey }) {
  const help = GUIDANCE_FIELD_HELP[fieldKey];
  if (!help) return null;
  return (
    <div className="mt-2 p-3 bg-blue-50/70 border border-blue-200/60 rounded-sm text-[11px] text-zinc-700 space-y-2 animate-in slide-in-from-top-1 duration-200">
      <p><span className="font-semibold text-zinc-900">Cuándo:</span> {help.when}</p>
      <p><span className="font-semibold text-zinc-900">Dónde se inyecta:</span> {help.injects}</p>
      <p><span className="font-semibold text-zinc-900">Qué controla:</span> {help.controls}</p>
      <div>
        <p className="font-semibold text-zinc-900 mb-1">Plantilla sugerida:</p>
        <pre className="p-2 bg-zinc-900 text-zinc-100 rounded-sm text-[10px] font-mono whitespace-pre-wrap overflow-x-auto">{help.template}</pre>
      </div>
      <p><span className="font-semibold text-zinc-900">Ejemplo:</span> {help.example}</p>
      {help.tip && <p className="text-blue-800">{help.tip}</p>}
    </div>
  );
}

function CapsuleGuidanceTab({ config, handleSave, saving }) {
  const [routing, setRouting] = useState('');
  const [ticketG, setTicketG] = useState('');
  const [ticketCoord, setTicketCoord] = useState('');
  const [slaG, setSlaG] = useState('');
  const [vocabG, setVocabG] = useState('');
  const [kbUsage, setKbUsage] = useState('');
  const [modeloTrabajo, setModeloTrabajo] = useState('');
  const [materializeOn, setMaterializeOn] = useState('classification');
  const [expandedHelp, setExpandedHelp] = useState(null);
  useEffect(() => {
    setRouting(config.routing_guidance || '');
    setTicketG(config.ticket_guidance || '');
    setTicketCoord(config.ticket_coordination_guidance || '');
    setSlaG(config.sla_followup_guidance || '');
    setVocabG(config.vocabulary_guidance || '');
    setKbUsage(config.kb_usage_guidance || '');
    setModeloTrabajo(config.modelo_de_trabajo || '');
    setMaterializeOn(config.ticket_create_policy?.materialize_on || 'classification');
  }, [config.routing_guidance, config.ticket_guidance, config.ticket_coordination_guidance, config.sla_followup_guidance, config.vocabulary_guidance, config.kb_usage_guidance, config.ticket_create_policy, config.modelo_de_trabajo]);

  const row = (title, hint, value, setVal, field, contentType) => (
    <div className="space-y-2 border border-zinc-200 rounded-sm p-3 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-zinc-900">{title}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">{hint}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[10px] border-blue-200 text-blue-700 hover:bg-blue-50"
            onClick={() => setExpandedHelp(expandedHelp === field ? null : field)}
            data-testid={`guidance-help-toggle-${field}`}
          >
            <HelpCircle className="w-3 h-3 mr-1" />
            {expandedHelp === field ? 'Ocultar ayuda' : '¿Qué poner aquí?'}
          </Button>
          <CapsuleAIGenerateButton contentType={contentType} onGenerated={setVal} />
        </div>
      </div>
      {expandedHelp === field && <GuidanceFieldHelpPanel fieldKey={field} />}
      <Textarea value={value} onChange={e => setVal(e.target.value)} rows={6} className="text-xs font-mono rounded-sm" />
      <Button type="button" size="sm" className="h-8 text-xs bg-brand-primary text-white" disabled={saving}
        onClick={() => handleSave(field, value)}>
        {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Guardar
      </Button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="capsule-guidance-tab">
      <InlineHelp defaultOpen>
        <p className="font-semibold text-[#09090B]">{GUIDANCE_TAB_INTRO.title}</p>
        {GUIDANCE_TAB_INTRO.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        <p className="mt-2">
          Flujo: clasificación previa → agente de línea → post-turno react_v1 si hay ticket.
          {' '}<CapsuleConfigHelpLink section="guias">Ver manual completo (sección Guías)</CapsuleConfigHelpLink>
        </p>
      </InlineHelp>
      <Card className="border border-indigo-200/70 rounded-sm">
        <CardHeader className="pb-2 border-b border-indigo-100/80">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-indigo-950 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Guias descriptivas (capsula)
          </CardTitle>
          <p className="text-[11px] text-zinc-600 font-normal normal-case pt-1">
            Texto Markdown inyectado en el clasificador y en el motor post-turno (react_v1).
            El comportamiento de negocio se define aqui y en soul/rules/skills de los agentes; no hay matrices de routing en la API.
          </p>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="space-y-2 border border-amber-200 rounded-sm p-3 bg-amber-50/40" data-testid="ticket-create-policy-card">
            <p className="text-xs font-semibold text-zinc-900">Crear ticket en BD (materialización)</p>
            <p className="text-[10px] text-zinc-600">
              Independiente del flag <code className="bg-white px-0.5">create_ticket</code> del JSON de clasificación.
              Con &quot;solo el agente&quot;, ese flag es señal/propuesta: el ticket nace con <code className="bg-white px-0.5">omnisys_ticket_create</code> tras relevamiento (RULES).
            </p>
            <select
              className="border border-brand-border rounded-sm p-1.5 text-xs h-8 w-full max-w-md bg-white"
              value={materializeOn}
              onChange={(e) => setMaterializeOn(e.target.value)}
              data-testid="ticket-create-materialize-on"
            >
              <option value="classification">Al clasificar (inmediato)</option>
              <option value="agent_tool_only">Solo el agente con tool (tras relevamiento)</option>
            </select>
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs bg-brand-primary text-white"
              disabled={saving}
              onClick={() => handleSave('ticket_create_policy', {
                ...(config.ticket_create_policy || {}),
                materialize_on: materializeOn,
              })}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Guardar política de creación
            </Button>
          </div>
          {row('Modelo de trabajo del area', 'Como trabaja el equipo, en etapas. Se inyecta ANTES del SOUL de los cuatro agentes, asi que es el texto de mayor alcance de la capsula.', modeloTrabajo, setModeloTrabajo, 'modelo_de_trabajo', 'modelo_de_trabajo')}
          {row('Routing', 'Criterios para elegir suggested_agent en la primera clasificacion (incl. N1 apps vs infra si aplica).', routing, setRouting, 'routing_guidance', 'routing_guidance')}
          {row('Tickets (clasificacion)', 'Cuando proponer create_ticket en el JSON de clasificacion.', ticketG, setTicketG, 'ticket_guidance', 'ticket_guidance')}
          {row('Coordinacion en ticket (post-turno)', 'Blackboard, subtareas, delegar_agente; incluye seguimiento si enlazas con la guia SLA.', ticketCoord, setTicketCoord, 'ticket_coordination_guidance', 'ticket_coordination_guidance')}
          {row('SLA y seguimiento', 'Plazos, insistencia a operadores, tareas de prorroga, cierres; orientacion para el coordinador post-turno.', slaG, setSlaG, 'sla_followup_guidance', 'sla_followup_guidance')}
          {row('Vocabulario global', 'Criterios transversales para intent, category, label y priority.', vocabG, setVocabG, 'vocabulary_guidance', 'vocabulary_guidance')}
          {row('Uso de KB (gate llm)', 'Solo se usa si agent_runtime.kb_retrieval_gate = llm. Criterios de negocio para consultar o no la KB.', kbUsage, setKbUsage, 'kb_usage_guidance', 'kb_usage_guidance')}
        </CardContent>
      </Card>
    </div>
  );
}

export default function CapsuleConfigPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = user?.tenant_id || '';
  const [capsules, setCapsules] = useState([]);
  const [capsuleId, setCapsuleId] = useState('');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmState, setConfirmState] = useState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [rtKbGate, setRtKbGate] = useState('off');
  const [rtCoordRounds, setRtCoordRounds] = useState('4');
  const [rtMaxToolRounds, setRtMaxToolRounds] = useState('5');
  const [rtWallClock, setRtWallClock] = useState('120');
  const [rtMaxCtx, setRtMaxCtx] = useState('24');
  const [rtCtxNote, setRtCtxNote] = useState(true);
  const [rtAgenticEnforce, setRtAgenticEnforce] = useState(true);
  const [rtMaxActionRetries, setRtMaxActionRetries] = useState('2');
  const [rtHarnessGraph, setRtHarnessGraph] = useState(true);
  // Si los agentes de esta cápsula reciben la memoria de quien les escribe. Ausente es
  // que sí, igual que del lado del backend: una cápsula que nunca tocó la perilla se
  // comporta como el default y no como un vacío.
  const [memoriaDePersonas, setMemoriaDePersonas] = useState(true);
  const [aprendeAlCerrar, setAprendeAlCerrar] = useState(true);
  // Si los agentes de esta capsula pueden usar casos ya resueltos. Apaga las tres puertas:
  // el bloque de casos parecidos, la coleccion tickets del RAG y las dos herramientas.
  const [casosResueltos, setCasosResueltos] = useState(true);
  const [rtKbQuality, setRtKbQuality] = useState(true);
  const [rtTiers, setRtTiers] = useState({ read: true, write: true, external: true, destructive: false });
  const [catalogSkills, setCatalogSkills] = useState([]);
  const [policyDraft, setPolicyDraft] = useState([]);
  const [capsulesReady, setCapsulesReady] = useState(false);

  const activeTab = CONFIG_TABS.includes(searchParams.get('tab') || '')
    ? searchParams.get('tab')
    : 'coordinacion';

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setCapsulesReady(false);
    (async () => {
      try {
        const res = await capsulesAPI.list(tenantId);
        if (cancelled) return;
        const items = res.data || [];
        setCapsules(items);
        const q = (searchParams.get('capsule') || '').trim();
        const fromLs = (localStorage.getItem(CAPSULE_CFG_LS) || '').trim();
        let pick = q || fromLs || items[0]?.capsule_id || '';
        if (pick && !items.find((c) => c.capsule_id === pick)) {
          pick = items[0]?.capsule_id || '';
        }
        setCapsuleId(pick);
        if (pick) localStorage.setItem(CAPSULE_CFG_LS, pick);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setCapsulesReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, searchParams]);

  const onCapsuleChange = (id) => {
    setCapsuleId(id);
    if (id) {
      localStorage.setItem(CAPSULE_CFG_LS, id);
      const next = { capsule: id };
      if (activeTab !== 'coordinacion') next.tab = activeTab;
      setSearchParams(next, { replace: true });
    }
  };

  const onConfigTabChange = (tab) => {
    const next = { capsule: capsuleId };
    if (tab !== 'coordinacion') next.tab = tab;
    setSearchParams(next, { replace: true });
  };

  const loadConfig = useCallback(async () => {
    if (!tenantId || !capsuleId) {
      setConfig(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await capsuleConfigAPI.get(tenantId, capsuleId);
      setConfig(res.data);
      setPolicyDraft(res.data.skill_policy || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [tenantId, capsuleId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  useEffect(() => {
    if (!tenantId || !capsuleId) return;
    skillsAPI.list().then((r) => setCatalogSkills(r.data || [])).catch(() => setCatalogSkills([]));
  }, [tenantId, capsuleId]);

  useEffect(() => {
    if (!config) return;
    const ar = config.agent_runtime || {};
    setRtKbGate(ar.kb_retrieval_gate || 'off');
    setRtCoordRounds(String(ar.max_coordinator_tool_rounds ?? 4));
    setRtMaxToolRounds(String(ar.max_tool_rounds ?? 5));
    setRtWallClock(String(ar.wall_clock_timeout_sec_per_llm_call ?? 120));
    setRtMaxCtx(String(ar.max_messages_context ?? 24));
    setRtCtxNote(ar.context_note_omitted_messages !== false);
    setRtAgenticEnforce(ar.agentic_enforce_enabled !== false);
    setRtMaxActionRetries(String(ar.max_action_retries ?? 2));
    setRtHarnessGraph(ar.harness_graph_emit !== false);
    setMemoriaDePersonas((config.memoria_de_personas || {}).enabled !== false);
    setAprendeAlCerrar((config.memoria_de_personas || {}).aprende_al_cerrar !== false);
    setCasosResueltos((config.casos_resueltos || {}).enabled !== false);
    setRtKbQuality(ar.agentic_kb_quality_enabled !== false);
    const tiers = Array.isArray(ar.allowed_tool_risk_tiers) ? ar.allowed_tool_risk_tiers : ['read', 'write', 'external'];
    setRtTiers({
      read: tiers.includes('read'),
      write: tiers.includes('write'),
      external: tiers.includes('external'),
      destructive: tiers.includes('destructive'),
    });
  }, [config]);

  const skillPolicyRows = useMemo(() => {
    const pmap = Object.fromEntries((policyDraft || []).map((p) => [p.skill_id, !!p.enabled]));
    const ids = new Set([
      ...Object.keys(pmap),
      ...(catalogSkills || []).map((s) => s.skill_id),
    ]);
    return Array.from(ids).sort().map((sid) => {
      const meta = catalogSkills.find((s) => s.skill_id === sid);
      return { skill_id: sid, name: meta?.name || sid, enabled: pmap[sid] ?? false };
    });
  }, [policyDraft, catalogSkills]);

  const toggleSkillPolicy = (skillId, enabled) => {
    setPolicyDraft((prev) => {
      const rest = (prev || []).filter((p) => p.skill_id !== skillId);
      return [...rest, { skill_id: skillId, enabled }];
    });
  };

  const handleSave = async (section, data) => {
    setSaving(true);
    try {
      const res = await capsuleConfigAPI.update(tenantId, capsuleId, { [section]: data });
      setConfig(res.data);
      if (Array.isArray(res.data?.skill_policy)) {
        setPolicyDraft(res.data.skill_policy);
      }
      toast.success('Configuracion guardada');
    // El backend rechaza con un texto que dice **que** falta ("Falta marcar el estado inicial
    // (es_inicial): con cual nace la unidad de trabajo."). Aca se mostraba "Error al guardar",
    // asi que todo el trabajo de `validar_coherencia` y `validar_intents` no llegaba a nadie:
    // el usuario veia un error mudo y no tenia como saber que corregir.
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    setConfirmState({
      open: true,
      title: 'Restaurar configuracion por defecto',
      description: 'Se perderan los cambios personalizados de la capsula actual.',
      confirmText: 'Restaurar',
      onConfirm: async () => {
        try {
          const res = await capsuleConfigAPI.reset(tenantId, capsuleId);
          setConfig({ ...res.data.config, tenant_id: tenantId, capsule_id: capsuleId, is_default: true });
          toast.success('Configuracion restaurada a valores por defecto');
        } catch (err) { toast.error('Error al restaurar'); }
      },
    });
  };

  if (capsulesReady && !capsules.length) {
    return (
      <div className="p-6 max-w-7xl mx-auto" data-testid="capsule-config-page">
        <h1 className="text-xl font-bold tracking-tight text-[#09090B]">CONFIGURACION CAPSULA</h1>
        <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-sm p-4">
          No hay cápsulas en este tenant. Crea la primera en el menú <strong>Cápsulas</strong>; al crearla se cargan habilidades y plantilla de configuración.
        </p>
      </div>
    );
  }

  if (loading || !config) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="capsule-config-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1">
          <h1 className="text-xl font-bold tracking-tight text-[#09090B]">CONFIGURACION CAPSULA</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Label className="text-xs text-zinc-500 whitespace-nowrap">Cápsula</Label>
            <select
              data-testid="capsule-config-selector"
              value={capsuleId}
              onChange={(e) => onCapsuleChange(e.target.value)}
              className="border border-zinc-200 rounded-sm text-sm px-2 py-1.5 max-w-xs bg-white"
            >
              {capsules.map((c) => (
                <option key={c.capsule_id} value={c.capsule_id}>{c.capsule_id} — {c.name}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-zinc-400 mt-2 uppercase tracking-[0.15em]">
            {capsuleId} — Clasificacion dinamica (soul/rules/skills del coordinador); motor post-turno react_v1
          </p>
        </div>
        <div className="flex gap-2">
          {config.is_default && <Badge variant="outline" className="text-[10px] rounded-sm border-amber-300 text-amber-600">Config por defecto</Badge>}
          <Button variant="outline" size="sm" onClick={handleReset} className="rounded-sm text-xs h-8">
            <RotateCcw className="w-3 h-3 mr-1" /> Restaurar Defaults
          </Button>
          <CapsuleConfigHelp />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={onConfigTabChange} className="space-y-4">
        <TabsList className="bg-zinc-100 rounded-sm p-1 h-9 flex flex-wrap gap-0.5">
          <TabsTrigger value="coordinacion" data-testid="tab-coordinacion" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Layers className="w-3 h-3 mr-1" /> Coordinacion
          </TabsTrigger>
          <TabsTrigger value="guias" data-testid="tab-guias" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <FileText className="w-3 h-3 mr-1" /> Guias capsula
          </TabsTrigger>
          <TabsTrigger value="trabajo" data-testid="tab-trabajo" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Layers className="w-3 h-3 mr-1" /> Plantillas
          </TabsTrigger>
          <TabsTrigger value="capacidades" data-testid="tab-capacidades" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Sliders className="w-3 h-3 mr-1" /> Capacidades
          </TabsTrigger>
          <TabsTrigger value="criterio" data-testid="tab-criterio" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Compass className="w-3 h-3 mr-1" /> Criterio
          </TabsTrigger>
          <TabsTrigger value="avisos" data-testid="tab-avisos" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Bell className="w-3 h-3 mr-1" /> Avisos
          </TabsTrigger>
          <TabsTrigger value="pantallas" data-testid="tab-pantallas" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Eye className="w-3 h-3 mr-1" /> Quién ve qué
          </TabsTrigger>
          <TabsTrigger value="skills" data-testid="tab-skills-policy" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Wrench className="w-3 h-3 mr-1" /> Habilidades (cápsula)
          </TabsTrigger>
          <TabsTrigger value="labels" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Tag className="w-3 h-3 mr-1" /> Labels
          </TabsTrigger>
          <TabsTrigger value="intents" data-testid="intents-tab" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Zap className="w-3 h-3 mr-1" /> Intents
          </TabsTrigger>
          <TabsTrigger value="llm" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <KeyRound className="w-3 h-3 mr-1" /> LLM Tenant
          </TabsTrigger>
          <TabsTrigger value="kb" data-testid="tab-kb" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
            <Database className="w-3 h-3 mr-1" /> Conocimiento
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="api" data-testid="tab-api" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
              <KeyRound className="w-3 h-3 mr-1" /> API
            </TabsTrigger>
          )}
        </TabsList>

        {user?.role === 'admin' && (
          <TabsContent value="api" data-testid="tab-api-content">
          <QueSeConfiguraAca
            testId="ayuda-api"
            queEs="Llaves para leer datos de esta cápsula desde afuera, sin usar la pantalla."
            paraQue="Sirve para armar un tablero propio, exportar a otra herramienta, o mirar un caso desde una terminal sin abrir el navegador."
            aQueAfecta={<>Sólo a <b>lectura</b>, y acotado a esta cápsula. Un token no puede escribir nada: se hace cumplir mirando el método HTTP, no una lista de rutas que alguien tenga que mantener al día.</>}
            ejemplo={<>Traer el contexto completo de un caso en JSON para revisarlo o archivarlo.</>}
            ojo={<>El token <b>se muestra una sola vez</b> y no se puede recuperar: del lado del servidor sólo queda su hash. Si se pierde, se genera otro.</>}
          />

            <CapsuleApiTokensPanel tenantId={tenantId} capsuleId={capsuleId} />
          </TabsContent>
        )}

        <TabsContent value="coordinacion" data-testid="tab-coordinacion-content" className="space-y-4">
          <QueSeConfiguraAca
            testId="ayuda-coordinacion"
            queEs="Cómo se decide, antes de responder, de qué se trata un mensaje y quién lo atiende."
            paraQue="Cada mensaje que entra pasa primero por el coordinador: clasifica de qué es, decide si abre un caso y a qué agente lo manda. Acá se ajusta cuánto puede trabajar en esa decisión."
            aQueAfecta={<>A <b>todos los mensajes que entran</b> por cualquier canal, antes de que un agente conteste. Es lo primero que corre y lo que decide todo lo que pasa después.</>}
            ejemplo={<>Subir las rondas del coordinador le da más margen para investigar antes de decidir; bajarlas lo hace más rápido y más impreciso. Cada ronda es un turno del modelo y se paga en tiempo y en tokens.</>}
            ojo={<>Si las listas de <i>labels</i> e <i>intents</i> están vacías, el coordinador clasifica con <b>vocabulario libre</b> — inventa las categorías. Eso hace imposible medir después, porque cada caso se clasifica distinto.</>}
          />

          <Card className="rounded-sm border-indigo-200/70 bg-gradient-to-br from-indigo-50/50 via-white to-white shadow-sm">
            <CardHeader className="pb-2 border-b border-indigo-100/80">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-indigo-950">
                Modo de operacion
              </CardTitle>
              <p className="text-[11px] text-zinc-600 font-normal normal-case tracking-normal leading-relaxed pt-1">
                La clasificacion previa de cada mensaje usa el agente <span className="font-mono">coordinator</span> en Mongo:
                configuraciones <span className="font-mono">soul</span>, <span className="font-mono">rules</span> y habilidades de instruccion (p. ej. <span className="font-mono">secure-agent-ops</span>),
                mas los agentes activos de la capsula. Las listas de labels/intents vacias implican vocabulario libre en el JSON de salida.
              </p>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-zinc-800 pt-4">
              <InlineHelp defaultOpen>
                <p>
                  Tras cada respuesta del agente de linea en hilos con ticket, el motor <code className="bg-zinc-100 px-1 rounded">react_v1</code> ejecuta al coordinador con herramientas de plataforma (blackboard, subtareas, delegacion, HITL). No existe modo legacy en API.
                </p>
                <p className="font-semibold text-[#09090B] mt-2">{COORDINACION_INLINE_EXTRA.title}</p>
                <ul className="list-disc ml-4 space-y-0.5">
                  {COORDINACION_INLINE_EXTRA.rows.map((r) => (
                    <li key={r.param}><code className="bg-zinc-100 px-1 rounded">{r.param}</code> — {r.effect}</li>
                  ))}
                </ul>
                <p className="mt-2">
                  <CapsuleConfigHelpLink section="coordinacion">Ver sección Coordinación del manual</CapsuleConfigHelpLink>
                </p>
              </InlineHelp>
              <div className="rounded-sm border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-700">
                <span className="font-semibold text-zinc-900">Motor post-turno:</span>{' '}
                <code className="bg-zinc-100 px-1 rounded">{(config.coordinator_engine || 'react_v1')}</code>
                <span className="text-zinc-500"> (unico valor admitido: react_v1)</span>
              </div>
              <Card className="rounded-sm border border-zinc-200 bg-white shadow-none">
                <CardHeader className="py-3 pb-2 border-b border-zinc-100">
                  <CardTitle className="text-xs font-semibold uppercase tracking-wider text-zinc-800">
                    Politica agent_runtime (capsula)
                  </CardTitle>
                  <p className="text-[10px] text-zinc-500 font-normal normal-case">
                    Fusion con tenant <code className="bg-zinc-100 px-0.5">settings.agent_runtime</code>. Afecta bucle de tools del agente, ventana de mensajes, gate KB y rondas del coordinador post-turno.
                    El techo <code className="bg-zinc-100 px-0.5">http_turn_budget_sec</code> es de plataforma (solo lectura; env <code className="bg-zinc-100 px-0.5">HTTP_TURN_BUDGET_SEC</code>, default 600): ticket chat, axios y nginx host deben respetarlo.
                  </p>
                </CardHeader>
                <CardContent className="space-y-3 pt-3 text-xs">
                  <div className="rounded-sm border border-blue-100 bg-blue-50/60 px-2 py-1.5 text-[10px] text-blue-900">
                    <strong>Presupuesto HTTP de turno (plataforma):</strong> los turnos sync (Asistente de ticket, webchat)
                    no pueden superar este techo aunque subas rondas o wall_clock LLM. Si ves 504, revisá nginx host
                    <code className="bg-white/80 px-0.5 mx-0.5">proxy_read_timeout</code> ≥ techo y Capsula/Runtime.
                  </div>
                  <div>
                    <Label className="text-[11px]">kb_retrieval_gate</Label>
                    <p className="text-[10px] text-zinc-500 mb-1">Con skill knowledge-search: off = siempre busqueda vectorial; llm = clasificador JSON antes de RAG (fail-open si falla el LLM).</p>
                    <select
                      className="border border-brand-border rounded-sm p-1.5 text-xs h-8 w-full max-w-xs"
                      value={rtKbGate}
                      onChange={e => setRtKbGate(e.target.value)}
                    >
                      <option value="off">off</option>
                      <option value="llm">llm</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px]">max_tool_rounds</Label>
                    <p className="text-[10px] text-zinc-500 mb-1">Rondas de tools HTTP + workspace del agente conversacional (1–20, default 5). Si ves max_tool_rounds_reached en Logs/Turn Health, subi este valor.</p>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      className="h-8 text-xs max-w-[120px] rounded-sm"
                      value={rtMaxToolRounds}
                      onChange={e => setRtMaxToolRounds(e.target.value)}
                    />
                  </div>
                    <div className="rounded-sm border border-emerald-100 bg-emerald-50/50 px-2 py-2 space-y-2">
                    <p className="text-[10px] font-semibold text-emerald-900 uppercase tracking-wide">Harness agentico (DT-048 / DT-049)</p>
                    <div className="flex items-center justify-between gap-2 max-w-md">
                      <div>
                        <Label className="text-[11px]">agentic_enforce_enabled</Label>
                        <p className="text-[10px] text-zinc-500">Si hay ActionRequired, no cerrar el turno en prosa sin tools. Apagá para comportamiento legacy.</p>
                      </div>
                      <Switch checked={rtAgenticEnforce} onCheckedChange={setRtAgenticEnforce} />
                    </div>
                    <div>
                      <Label className="text-[11px]">max_action_retries</Label>
                      <p className="text-[10px] text-zinc-500 mb-1">Reintentos cuando el modelo narra en vez de emitir tool_calls (1–3).</p>
                      <Input
                        type="number"
                        min={1}
                        max={3}
                        className="h-8 text-xs max-w-[120px] rounded-sm"
                        value={rtMaxActionRetries}
                        onChange={e => setRtMaxActionRetries(e.target.value)}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2 max-w-md">
                      <div>
                        <Label className="text-[11px]">harness_graph_emit</Label>
                        <p className="text-[10px] text-zinc-500">Emitir harness_turn_graph / harness_control en Autopsia → Workflow.</p>
                      </div>
                      <Switch checked={rtHarnessGraph} onCheckedChange={setRtHarnessGraph} />
                    </div>
                    <div className="flex items-center justify-between gap-2 max-w-md">
                      <div>
                        <Label className="text-[11px]">agentic_kb_quality_enabled</Label>
                        <p className="text-[10px] text-zinc-500">DT-049: anti meta-narración KB (“voy a consultar…”) + ActionRequired support_kb_turn en incidencias.</p>
                      </div>
                      <Switch checked={rtKbQuality} onCheckedChange={setRtKbQuality} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px]">max_coordinator_tool_rounds</Label>
                    <p className="text-[10px] text-zinc-500 mb-1">Rondas LLM+herramientas del coordinador react_v1 por pasada (tope duro 10).</p>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      className="h-8 text-xs max-w-[120px] rounded-sm"
                      value={rtCoordRounds}
                      onChange={e => setRtCoordRounds(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-[11px]">wall_clock_timeout_sec_per_llm_call</Label>
                    <p className="text-[10px] text-zinc-500 mb-1">Timeout por llamada LLM del agente (15–600s, default 120). Debe caber bajo el techo HTTP de plataforma.</p>
                    <Input
                      type="number"
                      min={15}
                      max={600}
                      className="h-8 text-xs max-w-[120px] rounded-sm"
                      value={rtWallClock}
                      onChange={e => setRtWallClock(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-[11px]">max_messages_context</Label>
                    <p className="text-[10px] text-zinc-500 mb-1">Ventana de mensajes recientes inyectados al prompt (4–100, default 24).</p>
                    <Input
                      type="number"
                      min={4}
                      max={100}
                      className="h-8 text-xs max-w-[120px] rounded-sm"
                      value={rtMaxCtx}
                      onChange={e => setRtMaxCtx(e.target.value)}
                    />
                  </div>
                  <label className="flex items-start gap-2 text-[11px] text-zinc-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={rtCtxNote}
                      onChange={(e) => setRtCtxNote(e.target.checked)}
                    />
                    <span>
                      <span className="font-medium">context_note_omitted_messages</span>
                      <span className="block text-[10px] text-zinc-500">Si hay mas mensajes que la ventana, anteponer nota + primer mensaje de usuario.</span>
                    </span>
                  </label>
                  <div>
                    <Label className="text-[11px]">allowed_tool_risk_tiers</Label>
                    <p className="text-[10px] text-zinc-500 mb-1">Que tiers de tools puede ejecutar el agente. <strong className="text-amber-800">destructive</strong> habilita operaciones destructivas (default OFF).</p>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {['read', 'write', 'external', 'destructive'].map((tier) => (
                        <label key={tier} className={`flex items-center gap-1.5 text-[11px] ${tier === 'destructive' ? 'text-amber-900' : 'text-zinc-700'}`}>
                          <input
                            type="checkbox"
                            checked={!!rtTiers[tier]}
                            onChange={(e) => setRtTiers((t) => ({ ...t, [tier]: e.target.checked }))}
                          />
                          {tier}
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 text-xs bg-brand-primary text-white"
                    disabled={saving}
                    onClick={() => {
                      const tiers = ['read', 'write', 'external', 'destructive'].filter((t) => rtTiers[t]);
                      handleSave('agent_runtime', {
                        ...(config.agent_runtime || {}),
                        kb_retrieval_gate: rtKbGate,
                        max_tool_rounds: Math.min(20, Math.max(1, parseInt(rtMaxToolRounds, 10) || 5)),
                        max_coordinator_tool_rounds: Math.min(10, Math.max(1, parseInt(rtCoordRounds, 10) || 4)),
                        wall_clock_timeout_sec_per_llm_call: Math.min(600, Math.max(15, parseInt(rtWallClock, 10) || 120)),
                        max_messages_context: Math.min(100, Math.max(4, parseInt(rtMaxCtx, 10) || 24)),
                        context_note_omitted_messages: !!rtCtxNote,
                        agentic_enforce_enabled: !!rtAgenticEnforce,
                        max_action_retries: Math.min(3, Math.max(1, parseInt(rtMaxActionRetries, 10) || 2)),
                        harness_graph_emit: !!rtHarnessGraph,
                        agentic_kb_quality_enabled: !!rtKbQuality,
                        allowed_tool_risk_tiers: tiers.length ? tiers : ['read', 'write', 'external'],
                      });
                    }}
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                    Guardar politica runtime
                  </Button>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="guias" data-testid="tab-guias-content">
          <QueSeConfiguraAca
            testId="ayuda-guias"
            queEs="Los textos que el motor le pega a cada tarea interna: cómo enrutar, cómo armar un caso, cuándo escalar."
            paraQue="Son instrucciones para partes específicas del motor, no criterio general del área. Se separaron del criterio justamente porque son dos cosas distintas que antes se llamaban igual."
            aQueAfecta={<>A <b>sub-tareas del motor</b>, no al agente que conversa. Tocar la guía de enrutamiento cambia cómo se decide a quién va un caso; no cambia cómo responde nadie.</>}
            ejemplo={<>Si querés que un agente actúe con otro criterio, eso no va acá: va en <i>Criterio</i>. Acá va cómo el motor arma el ticket o clasifica un mensaje.</>}
            ojo={<>No confundir con <b>Criterio</b>, que es la prosa del área. Que las dos se llamaran «guía» fue una de las confusiones que este rediseño vino a deshacer.</>}
          />

          <CapsuleGuidanceTab config={config} handleSave={handleSave} saving={saving} />
        </TabsContent>

        <TabsContent value="trabajo" data-testid="tab-trabajo-content">
          <QueSeConfiguraAca
            testId="ayuda-plantillas"
            queEs="Las tareas que se crean solas cuando entra un caso de cierto tipo."
            paraQue="Los pedidos que se repiten tienen siempre los mismos pasos. Declararlos una vez evita que alguien los arme a mano cada vez, y que se olvide uno."
            aQueAfecta={<>A los <b>casos nuevos</b> de ese tipo: al clasificarse, se les crean las tareas de la plantilla. No toca los casos que ya existen.</>}
            ejemplo={<>Un pedido de desarrollo genera análisis, desarrollo, prueba y pase. Cada subtarea declara <b>un rol y no una persona</b>: así la plantilla sigue sirviendo cuando cambia el equipo.</>}
            ojo={<>Si el esfuerzo declarado en las fases no suma bien, se avisa al guardar. Una plantilla con horas mal repartidas hace que todas las estimaciones del área salgan torcidas.</>}
          />

          <CapsuleWorkflowTab config={config} handleSave={handleSave} saving={saving} capsuleIntents={config.intents || []} teamRoles={config.team_roles || []} />
        </TabsContent>

        {/* Con que valores corre cada capacidad del producto aca. Va antes que el criterio
            porque es el orden en que alguien lo piensa: primero con que numeros trabaja, y
            despues con que criterio. */}
        <TabsContent value="capacidades" data-testid="tab-capacidades-content" className="space-y-4">
          <ParametrosDeCapacidad tenantId={tenantId} capsuleId={capsuleId} />
        </TabsContent>

        {/* El criterio del area. **No es lo mismo que "Guias capsula"**, que son los textos que
            el motor le pega a cada sub-prompt (routing, ticket, SLA): eso es
            configuracion del motor. Esto es prosa de la organizacion, y se suma a la del tenant
            y a la del agente. Que dos cosas distintas se llamaran "guia" en la misma pantalla
            era exactamente lo que este plan viene corrigiendo. */}
        {/* Que pantalla ve cada rol. Va despues del criterio y antes de las skills porque es la
            unica solapa que no habla de como trabajan los agentes sino de quien entra a la
            consola — y esa es una decision de cada organizacion, no del producto. */}
        <TabsContent value="pantallas" data-testid="tab-pantallas-content" className="space-y-4">
          <PantallasPorRol tenantId={tenantId} capsuleId={capsuleId} />
        </TabsContent>

        <TabsContent value="criterio" data-testid="tab-criterio-content" className="space-y-4">
          <Criterios
            criterios={config?.criterios || []}
            onGuardar={(cs) => handleSave('criterios', cs)}
            guardando={saving}
            alcance="capsula"
          />
        </TabsContent>

        {/* Lo que la capsula le avisa a la gente. Va al lado de las plantillas y no adentro de
            Coordinacion porque es una decision de trato con quien pidio el caso, no de motor. */}
        <TabsContent value="avisos" data-testid="tab-avisos-content" className="space-y-4">
          {/* Va **arriba** del catálogo: define qué se pregunta, y el catálogo define cuándo.
              La encuesta no se dispara sola — viaja adentro de un aviso. */}
          <LaConformidad config={config} handleSave={handleSave} saving={saving} />
          <AvisosDeLaCapsula config={config} handleSave={handleSave} saving={saving} />
        </TabsContent>

        <TabsContent value="skills" data-testid="tab-skills-policy-content" className="space-y-4">
          <QueSeConfiguraAca
            testId="ayuda-skills-capsula"
            queEs="Qué habilidades del catálogo de la organización puede usar esta área."
            paraQue="El catálogo es del tenant y lo comparten todas las cápsulas. Esto es el filtro del área: lo que no pasa por acá, ningún agente de esta cápsula puede tener."
            aQueAfecta={<>A qué se le puede <b>asignar</b> a un agente. <b>Permitir no es asignar</b>: para que un agente la use hay que dársela además en <i>Agentes → Qué puede hacer</i>. Por eso una habilidad puede verse permitida acá y desactivada allá — son dos preguntas distintas.</>}
            ejemplo={<>Permitir «buscar en el conocimiento» habilita que los agentes de esta área la tengan; que la tengan o no se decide agente por agente.</>}
            ojo={<>Apagar acá una habilidad que un agente ya tiene <b>se la saca</b>, aunque en su pantalla siga marcada. El filtro del área manda.</>}
          />

          <InlineHelp>
            {SKILLS_TAB_INTRO.map((p, i) => <p key={i}>{p}</p>)}
            <p className="mt-2">
              <CapsuleConfigHelpLink section="skills">Ver sección Habilidades del manual</CapsuleConfigHelpLink>
            </p>
          </InlineHelp>
          <Card className="rounded-sm border-zinc-200">
            <CardHeader className="pb-2 border-b border-zinc-100">
              {/* **«Activa» acá y «activa» en Agentes son dos preguntas distintas**, y usar la
                  misma palabra para las dos confundió a Diego el 2026-08-25: veía la habilidad
                  activa acá y desactivada allá, y las dos pantallas decían la verdad. Acá se
                  responde *«¿la cápsula la deja pasar?»*; allá, *«¿este agente la tiene?»*. Se
                  puede permitir algo que nadie usa — y de hecho es el estado normal al empezar. */}
              <CardTitle className="text-sm font-semibold uppercase tracking-wider">
                Qué habilidades puede usar esta cápsula
              </CardTitle>
              <p className="text-[11px] text-zinc-600 font-normal normal-case pt-1">
                Esto es el <b>permiso del área</b>: sólo lo permitido acá puede asignarse a un
                agente. <b>Permitir no es asignar</b> — para que un agente la use hay que dárselas
                además en <i>Agentes → [el agente] → Qué puede hacer</i>. Lo nuevo del tenant llega
                sin permiso hasta que lo habilites.
              </p>
            </CardHeader>
            <CardContent className="pt-4 space-y-2">
              {skillPolicyRows.map((row) => (
                <div key={row.skill_id} className="flex items-center justify-between gap-3 border border-zinc-100 rounded-sm px-3 py-2 bg-white">
                  <div>
                    <p className="text-xs font-mono text-brand-primary">{row.skill_id}</p>
                    <p className="text-[11px] text-zinc-600">{row.name}</p>
                  </div>
                  <Switch
                    checked={row.enabled}
                    onCheckedChange={(v) => toggleSkillPolicy(row.skill_id, v)}
                  />
                </div>
              ))}
              {!skillPolicyRows.length && (
                <p className="text-xs text-zinc-500">Cargando catálogo de habilidades…</p>
              )}
              <Button
                type="button"
                size="sm"
                className="mt-2 bg-brand-primary text-white rounded-sm text-xs"
                disabled={saving}
                onClick={() => handleSave('skill_policy', policyDraft)}
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Guardar política de skills
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LABELS TAB */}
        <TabsContent value="labels" data-testid="labels-tab">
          <QueSeConfiguraAca
            testId="ayuda-labels"
            queEs="Las etiquetas con las que se clasifican los casos de esta área."
            paraQue="Es el vocabulario del área. Sin una lista, el coordinador inventa las categorías y cada caso queda clasificado distinto — con eso no se puede medir nada."
            aQueAfecta={<>A la <b>clasificación</b> de cada caso nuevo y a los cortes del tablero. <b>No cambia el comportamiento de los agentes</b>: es organización, no instrucción.</>}
            ejemplo={<>Agregar «facturación» hace que el coordinador pueda usarla; sacarla no reclasifica los casos viejos, que la conservan.</>}
          />

          <LabelsEditor labels={config.labels || []} onSave={(d) => handleSave('labels', d)} saving={saving} />
        </TabsContent>

        {/* INTENTS TAB */}
        <TabsContent value="intents" data-testid="intents-tab-content">
          <QueSeConfiguraAca
            testId="ayuda-intents"
            queEs="Los tipos de pedido que atiende esta área, y qué se promete para cada uno."
            paraQue="No es lo mismo un incidente que frena la producción que una consulta. Declararlos permite prometer tiempos distintos y medir cumplimiento por separado."
            aQueAfecta={<>Al <b>compromiso de cada caso</b>: de acá sale la fecha que se le promete a quien pidió, el semáforo que ve, y el cumplimiento del tablero. También al presupuesto de investigación: cuánto puede trabajar un agente en ese tipo de pedido.</>}
            ejemplo={<>Un «bug de aplicación» con SLA de 8 horas hábiles produce un compromiso distinto que una «consulta funcional» de 48. Las horas son <b>hábiles</b>, contra el calendario del área.</>}
            ojo={<>Acá abajo está también el <b>ciclo de vida</b>: los estados y sus banderas. Cambiarle las banderas a un estado cambia cómo se mide todo lo que ya pasó por él.</>}
          />

          <IntentsEditor
            intents={config.intents || []}
            categories={config.categories || []}
            priorities={config.priorities || []}
            ticketStatuses={config.ticket_statuses || []}
            departments={config.requesting_departments || []}
            workflowTemplates={config.workflow_templates || []}
            teamRoles={config.team_roles || []}
            horarioLaboral={config.horario_laboral || null}
            sla={config.sla || null}
            defaultsVisibles={config.defaults_visibles || null}
            onSaveHorario={(d) => handleSave('horario_laboral', d)}
            onSaveSla={(d) => handleSave('sla', d)}
            onSaveIntents={(d) => handleSave('intents', d)}
            onSaveCategories={(d) => handleSave('categories', d)}
            onSavePriorities={(d) => handleSave('priorities', d)}
            onSaveTicketStatuses={(d) => handleSave('ticket_statuses', d)}
            onSaveTeamRoles={(d) => handleSave('team_roles', d)}
            onSaveDepartments={(d) => handleSave('requesting_departments', d)}
            saving={saving}
          />
        </TabsContent>

        <TabsContent value="llm" data-testid="llm-tenant-tab">
          <QueSeConfiguraAca
            testId="ayuda-llm"
            queEs="Qué modelo usa cada cosa: el que conversa, el que clasifica, el que rerankea."
            paraQue="No todas las tareas necesitan el mismo modelo. Clasificar o rerankear son tareas mecánicas: un modelo que razona tarda cuatro veces más para el mismo resultado."
            aQueAfecta={<>Al <b>costo y a la velocidad</b> de cada turno, y a la calidad de las respuestas. Un modelo más chico en el coordinador acelera todo sin tocar la conversación.</>}
            ejemplo={<>Un modelo que razona para el agente de línea y uno rápido para clasificar. En tareas mecánicas, <code>deepseek-chat</code> hace en 1 segundo lo que otro hace en 4.</>}
            ojo={<>El tope de tokens cubre <b>razonamiento más respuesta</b>, y el razonamiento va primero. Con el techo corto, el modelo piensa y se queda sin lugar para contestar: devuelve vacío. Subirlo sube también lo que piensa, así que no es una palanca confiable.</>}
          />

          <InlineHelp>
            {LLM_TAB_INTRO.map((p, i) => <p key={i}>{p}</p>)}
            <p className="mt-2">
              <CapsuleConfigHelpLink section="llm">Ver sección LLM Tenant del manual</CapsuleConfigHelpLink>
            </p>
          </InlineHelp>
          <TenantLLMProvidersEditor tenantId={tenantId} />
        </TabsContent>

        <TabsContent value="kb" data-testid="tab-kb-content">
          <QueSeConfiguraAca
            testId="ayuda-kb"
            queEs="Todo lo que un agente de esta área puede saber además de lo que sabe el modelo: la base de conocimiento (código y documentación), los casos ya resueltos, y lo que el área sabe de cada persona."
            paraQue="Sin esto, el agente contesta sólo con lo que sabe el modelo. Con esto arranca con la evidencia de la organización adelante."
            aQueAfecta={<>A <b>qué evidencia recibe el agente</b> en cada turno y cuánto contexto se paga. Son <b>tres fuentes distintas</b> y cada una tiene su interruptor acá abajo: apagar una no apaga las otras.</>}
            ejemplo={<>Prender la investigación iterativa le permite buscar varias veces afinando, en vez de una sola. Cada ronda es un turno del modelo, no una consulta suelta.</>}
            ojo={<>Si el agente no tiene la habilidad de búsqueda asignada, la parte de la base de conocimiento no hace nada por más que esté configurada. Los casos resueltos y la memoria de las personas <b>no</b> dependen de esa habilidad.</>}
          />

          <CapsuleKBAccessPanel capsuleId={capsuleId} />

          <Card className="border-brand-border rounded-sm mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Casos ya resueltos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <QueSeConfiguraAca
                testId="ayuda-casos-resueltos-capsula"
                queEs="Si los agentes de esta area pueden mirar lo que ya se resolvio antes: los casos de quien escribe, o los de cualquiera."
                paraQue="Que no se vuelva a diagnosticar de cero algo que el area ya resolvio tres veces."
                aQueAfecta={<>Apaga <b>las tres puertas</b> de una vez: el bloque de casos parecidos que entra solo, la coleccion de casos del buscador, y las dos herramientas. Cada agente ademas elige su alcance en «Que puede hacer».</>}
                ejemplo={<>Escribe alguien con un error de facturacion y el agente encuentra los tres casos iguales que ya se resolvieron.</>}
                ojo={<>Aunque este prendido, <b>si en la conversacion hay alguien de afuera de la organizacion</b> no entra ningun caso de terceros. Eso no se configura: se deriva de quien esta leyendo.</>}
              />
              <div className="flex items-center justify-between gap-2 max-w-xl">
                <div>
                  <Label className="text-[11px]">Los agentes de esta area pueden usar casos ya resueltos</Label>
                  <p className="text-[10px] text-zinc-500">
                    Apagado, ningun agente de esta capsula recibe nada de casos anteriores
                    --ni buscandolo ni sin buscarlo--, sin importar lo que diga cada agente.
                  </p>
                </div>
                <Switch
                  data-testid="interruptor-casos-resueltos"
                  checked={casosResueltos}
                  onCheckedChange={(v) => {
                    setCasosResueltos(v);
                    handleSave('casos_resueltos', { enabled: v });
                  }}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-brand-border rounded-sm mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Memoria de las personas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <QueSeConfiguraAca
                testId="ayuda-memoria-de-personas-capsula"
                queEs="Si los agentes de esta area reciben lo que el area sabe de quien les escribe: con que trabaja, sobre que suele pedir ayuda, y como conviene abordarlo."
                paraQue="Que el agente arranque sabiendo lo que sabria alguien del area que ya conoce a esa persona, en vez de preguntar de cero cada vez."
                aQueAfecta={<>Se inyecta <b>solo</b> en el turno de la persona que escribe. Ningun agente puede pedir la memoria de un tercero: no existe la herramienta. Cada persona ademas tiene su propio interruptor en su ficha.</>}
                ejemplo={<>Escribe Mauricio y el agente ya sabe que su tema mensual es la prevision de Distribuidores.</>}
                ojo={<>Son <b>dos interruptores</b> porque son dos decisiones: <b>usar</b> lo que ya se sabe, y <b>seguir aprendiendo</b>. Apagar el primero no borra ninguna memoria ni detiene lo que se anota; apagar el segundo deja las memorias como estan y no escribe mas.</>}
              />
              <div className="flex items-center justify-between gap-2 max-w-xl">
                <div>
                  <Label className="text-[11px]">Los agentes reciben la memoria de quien les escribe</Label>
                  <p className="text-[10px] text-zinc-500">
                    Apagado, ningun agente de esta capsula recibe memorias, ni siquiera las que
                    esten prendidas en la ficha de cada persona. No borra nada.
                  </p>
                </div>
                <Switch
                  data-testid="interruptor-memoria-de-personas"
                  checked={memoriaDePersonas}
                  onCheckedChange={(v) => {
                    setMemoriaDePersonas(v);
                    handleSave('memoria_de_personas', { enabled: v, aprende_al_cerrar: aprendeAlCerrar });
                  }}
                />
              </div>
              <div className="flex items-center justify-between gap-2 max-w-xl">
                <div>
                  <Label className="text-[11px]">Aprender de los casos que se cierran</Label>
                  <p className="text-[10px] text-zinc-500">
                    Al cerrarse un caso, el sistema anota hasta tres hechos sobre quien lo
                    pidio y cada tanto reescribe con ellos el parrafo de «aprendido de sus
                    casos». Es <b>una llamada al modelo por cierre</b>. Apagado, las memorias
                    que ya estan siguen sirviendo pero dejan de crecer.
                  </p>
                </div>
                <Switch
                  data-testid="interruptor-aprende-al-cerrar"
                  checked={aprendeAlCerrar}
                  onCheckedChange={(v) => {
                    setAprendeAlCerrar(v);
                    handleSave('memoria_de_personas', { enabled: memoriaDePersonas, aprende_al_cerrar: v });
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {user?.role === 'admin' && capsuleId && (
        <Card className="mt-8 rounded-sm border-red-200 bg-red-50/40" data-testid="capsule-danger-zone">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-red-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Zona peligrosa
            </CardTitle>
            <p className="text-[11px] text-red-900/80 font-normal normal-case">
              Solo administradores. Eliminación dura con doble confirmación en el diálogo.
            </p>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm border-red-300 text-red-800 hover:bg-red-100"
              onClick={() => setPurgeOpen(true)}
            >
              Eliminar cápsula y todos sus datos
            </Button>
          </CardContent>
        </Card>
      )}

      <PurgeCapsuleDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        tenantId={tenantId}
        capsuleId={capsuleId}
        capsuleName={capsules.find((c) => c.capsule_id === capsuleId)?.name}
        onSuccess={() => {
          try {
            if (localStorage.getItem(CAPSULE_CFG_LS) === capsuleId) {
              localStorage.removeItem(CAPSULE_CFG_LS);
            }
          } catch (_) { /* ignore */ }
          navigate('/capsules');
        }}
      />
      <ConfirmActionDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open) setConfirmState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
        }}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        onConfirm={confirmState.onConfirm}
      />
    </div>
  );
}

function TenantLLMProvidersEditor({ tenantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingRef, setTestingRef] = useState('');
  const [confirmState, setConfirmState] = useState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });

  const load = useCallback(async () => {
    try {
      const res = await tenantsAPI.listLLMProviders(tenantId);
      setItems((res.data || []).map(p => ({ ...p, api_key: '' })));
    } catch (err) {
      toast.error('Error cargando providers LLM del tenant');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const addItem = () => {
    setItems(prev => [...prev, {
      provider_ref: `provider-${Date.now()}`,
      provider: 'openai',
      model: 'gpt-4o-mini',
      enabled: true,
      models_available: [],
      api_key: '',
      is_new: true,
    }]);
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const saveItem = async (item) => {
    setSaving(true);
    try {
      const payload = {
        provider_ref: item.provider_ref,
        provider: item.provider,
        model: item.model,
        enabled: item.enabled,
        models_available: (item.models_available || []).filter(Boolean),
        api_key: item.api_key || null,
      };
      if (item.is_new) {
        await tenantsAPI.createLLMProvider(tenantId, payload);
      } else {
        await tenantsAPI.updateLLMProvider(tenantId, item.provider_ref, payload);
      }
      toast.success('Provider guardado');
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.detail || 'Error guardando provider'));
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (item) => {
    setConfirmState({
      open: true,
      title: 'Eliminar provider LLM',
      description: `Se eliminara el provider "${item.provider_ref}".`,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          await tenantsAPI.deleteLLMProvider(tenantId, item.provider_ref);
          toast.success('Provider eliminado');
          load();
        } catch {
          toast.error('Error eliminando provider');
        }
      },
    });
  };

  const testItem = async (item) => {
    setTestingRef(item.provider_ref);
    try {
      const res = await tenantsAPI.testLLMProvider(tenantId, item.provider_ref);
      if (res.data?.ok) toast.success(`Conexion OK (${res.data.provider}/${res.data.model})`);
      else toast.error(`${res.data?.error || 'ERROR'}: ${res.data?.message || 'Fallo de conexion'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.detail || 'Error probando conexion');
    } finally {
      setTestingRef('');
    }
  };

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <Card className="border border-brand-border rounded-sm">
      <CardHeader className="pb-3 border-b border-brand-border">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]">
            <KeyRound className="w-4 h-4 inline mr-1.5" /> Providers LLM por Tenant
          </CardTitle>
          <Button size="sm" variant="outline" onClick={addItem} className="rounded-sm text-xs h-7">
            <Plus className="w-3 h-3 mr-1" /> Agregar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {items.map((item, idx) => (
          <div key={item.provider_ref} className="border border-brand-border rounded-sm p-3 space-y-2">
            <div className="grid grid-cols-6 gap-2">
              <Input value={item.provider_ref} onChange={e => updateItem(idx, 'provider_ref', e.target.value)} className="text-xs h-8" />
              <select className="border border-brand-border rounded-sm p-1 text-xs h-8"
                value={item.provider} onChange={e => updateItem(idx, 'provider', e.target.value)}>
                <option value="openai">openai</option>
                <option value="anthropic">anthropic</option>
                <option value="google">google</option>
                <option value="groq">groq</option>
                <option value="grok">grok</option>
                <option value="deepseek">deepseek</option>
                <option value="moonshot">moonshot (Kimi)</option>
              </select>
              <Input value={item.model} placeholder="model (texto libre)" onChange={e => updateItem(idx, 'model', e.target.value)} className="text-xs h-8" />
              <Input value={item.models_available?.join(',') || ''} placeholder="modelos (coma)" onChange={e => updateItem(idx, 'models_available', e.target.value.split(',').map(v => v.trim()))} className="text-xs h-8" />
              <Input type="password" value={item.api_key || ''} placeholder={item.api_key_masked ? `Actual: ${item.api_key_masked}` : 'api key'} onChange={e => updateItem(idx, 'api_key', e.target.value)} className="text-xs h-8" />
              <div className="flex items-center gap-2">
                <Switch checked={item.enabled !== false} onCheckedChange={v => updateItem(idx, 'enabled', v)} />
                <span className="text-xs">{item.enabled !== false ? 'Activo' : 'Off'}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => saveItem(item)} disabled={saving} className="h-7 text-xs bg-brand-primary text-white">
                <Save className="w-3 h-3 mr-1" /> Guardar
              </Button>
              <Button size="sm" variant="outline" onClick={() => testItem(item)} disabled={testingRef === item.provider_ref} className="h-7 text-xs">
                {testingRef === item.provider_ref ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FlaskConical className="w-3 h-3 mr-1" />}
                Probar conexion
              </Button>
              <Button size="sm" variant="ghost" onClick={() => deleteItem(item)} className="h-7 text-xs text-red-600">
                <Trash2 className="w-3 h-3 mr-1" /> Eliminar
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
      <ConfirmActionDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open) setConfirmState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
        }}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        onConfirm={confirmState.onConfirm}
      />
    </Card>
  );
}

// ============================================================================
// LABELS EDITOR
// ============================================================================
function LabelsEditor({ labels, onSave, saving }) {
  const [items, setItems] = useState(labels);
  const addItem = () => setItems([...items, { id: '', name: '', color: '#6B7280', description: '' }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => setItems(items.map((item, idx) => idx === i ? { ...item, [field]: value } : item));

  return (
    <Card className="border border-brand-border rounded-sm">
      <CardHeader className="pb-3 border-b border-brand-border">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]"><Tag className="w-4 h-4 inline mr-1.5" /> Labels de Conversacion / Ticket</CardTitle>
          <Button size="sm" variant="outline" onClick={addItem} className="rounded-sm text-xs h-7"><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-2">
        <InlineHelp>
          <p className="font-semibold text-[#09090B]">Que son los Labels?</p>
          <p>Son etiquetas que se asignan a <strong>conversaciones</strong> y <strong>tickets</strong> para clasificarlos visualmente. Ejemplos: "Incidente", "Requerimiento", "Consulta".</p>
          <p className="font-semibold text-[#09090B] mt-2">Como se asignan?</p>
          <p>- <strong>Automaticamente</strong>: Segun el JSON de clasificacion del coordinador y las guias de capsula (puede incluir label en la salida).</p>
          <p>- <strong>Manualmente</strong>: Un supervisor puede cambiar el label desde el ticket.</p>
          <p className="font-semibold text-[#09090B] mt-2">Relacion con Agentes</p>
          <p>Los labels NO afectan directamente el comportamiento de los agentes. Son para <strong>organizacion visual</strong>. El comportamiento del agente depende de su SOUL.md, RULES.md y Habilidades asignadas (seccion Agentes).</p>
          <p className="mt-2 text-blue-600 font-medium">Campos: ID (identificador unico, sin espacios), Nombre (visible en UI), Color (visual), Descripcion.</p>
          <p className="mt-2">Lista vacía → el clasificador puede usar label libre en el JSON (vocabulario acotado por guías).</p>
          <p><CapsuleConfigHelpLink section="labels">Ver sección Labels del manual</CapsuleConfigHelpLink></p>
        </InlineHelp>
        <p className="text-[10px] text-zinc-400 mb-2">Los labels pueden venir de la clasificacion o asignarse manualmente desde ticket/conversacion.</p>
        {items.map((item, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`label-row-${i}`}>
            <Input className="col-span-2 rounded-sm text-xs h-8" placeholder="ID" value={item.id} onChange={e => updateItem(i, 'id', e.target.value)} />
            <Input className="col-span-3 rounded-sm text-xs h-8" placeholder="Nombre" value={item.name} onChange={e => updateItem(i, 'name', e.target.value)} />
            <input type="color" className="col-span-1 h-8 w-full rounded-sm cursor-pointer" value={item.color} onChange={e => updateItem(i, 'color', e.target.value)} />
            <Input className="col-span-5 rounded-sm text-xs h-8" placeholder="Descripcion" value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} />
            <Button variant="ghost" size="sm" onClick={() => removeItem(i)} className="col-span-1 h-8 text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}
        <Button onClick={() => onSave(items)} disabled={saving} className="mt-3 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs">
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Guardar Labels
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// INTENTS & CATEGORIES EDITOR
// ============================================================================
function slugifyIntent(value) {
  return (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Busca por id, nombre y criterio: el criterio es donde esta lo que uno recuerda del intent. */
function coincideIntent(item, q) {
  const s = (q || '').trim().toLowerCase();
  if (!s) return true;
  return [item.id, item.name, item.description]
    .some((v) => String(v || '').toLowerCase().includes(s));
}

function templateForIntent(workflowTemplates, intentId) {
  if (!intentId) return null;
  const norm = slugifyIntent(intentId);
  return (workflowTemplates || []).find((t) =>
    (t.intents || []).some((i) => slugifyIntent(i) === norm)
  ) || null;
}

const DIAS_SEMANA = [
  { n: 1, label: 'Lun' }, { n: 2, label: 'Mar' }, { n: 3, label: 'Mié' },
  { n: 4, label: 'Jue' }, { n: 5, label: 'Vie' }, { n: 6, label: 'Sáb' }, { n: 7, label: 'Dom' },
];

const DEFAULT_HORARIO_UI = {
  dias: [1, 2, 3, 4, 5], desde: '08:00', hasta: '17:00', pausa_minutos: 0,
  zona: 'America/Argentina/Buenos_Aires', feriados: [],
};

function IntentsEditor({
  intents,
  categories,
  priorities,
  ticketStatuses = [],
  departments = [],
  workflowTemplates = [],
  teamRoles = [],
  horarioLaboral = null,
  sla = null,
  defaultsVisibles = null,
  onSaveHorario,
  onSaveSla,
  onSaveIntents,
  onSaveCategories,
  onSavePriorities,
  onSaveTicketStatuses,
  onSaveDepartments,
  onSaveTeamRoles,
  saving,
}) {
  const [intentList, setIntentList] = useState(intents);
  const [catList, setCatList] = useState(categories);
  const [rolList, setRolList] = useState(teamRoles);
  const [prioList, setPrioList] = useState(priorities);
  const [statusList, setStatusList] = useState(ticketStatuses);
  // Se conserva el objeto entero, no {id, name}. `aliases` es lo que mapea los departamentos
  // que manda Entra ("SISTEMAS DESARROLLO") a la gerencia, y perderlos rompe la clasificacion
  // sin que nadie lo note.
  const [deptList, setDeptList] = useState((departments || []).map((d) => ({ ...d })));

  const [intentQ, setIntentQ] = useState('');
  //: Cual esta abierto. Uno solo: con 23 intents, abrir varios devuelve al muro de controles
  //: del que se venia.
  const [intentAbierto, setIntentAbierto] = useState(null);

  // El default sale del backend y no escrito aca, para que no haya dos numeros. Un campo
  // vacio que dice "el del sistema" no ayuda si no dice cual.
  const topeTokensDefault = defaultsVisibles?.investigacion_max_tokens;
  const topeTokensPlaceholder = topeTokensDefault ? topeTokensDefault.toLocaleString('es-AR') : 'por defecto';
  const topeTokensTitulo = topeTokensDefault
    ? `Techo de tokens de input por conversación. Vacío = ${topeTokensDefault.toLocaleString('es-AR')} (el del sistema).`
    : 'Techo de tokens de input por conversación. Vacío = el default del sistema.';

  // **El campo de rondas decía «sin tope» y eso era falso.** Vacío no es sin límite: manda
  // `max_tool_rounds` de la política de runtime del área. Un placeholder que promete que no
  // hay techo invita a dejarlo vacío creyendo que se acota con otra cosa, y lo que acota
  // después es el techo de tokens — que la propia calibración dice que casi corta un caso
  // legítimo porque las rondas lo habían frenado antes.
  const rondasDefault = defaultsVisibles?.investigacion_max_rondas;
  const rondasPlaceholder = rondasDefault != null ? String(rondasDefault) : 'general';
  const rondasTitulo = rondasDefault != null
    ? `Cuántos viajes de investigación admite este tipo de pedido. Vacío = ${rondasDefault}, que es lo que dice la política de runtime del área. 0 = no investiga: ni se le ofrecen las herramientas al modelo.`
    : 'Cuántos viajes de investigación admite este tipo de pedido. Vacío = lo que diga la política de runtime del área. 0 = no investiga.';

  const [horario, setHorario] = useState(horarioLaboral || DEFAULT_HORARIO_UI);
  const [modoDeAtencion, setModoDeAtencion] = useState(
    (sla || {}).atencion_se_detiene_con || 'mensaje_humano',
  );
  useEffect(() => { setHorario(horarioLaboral || DEFAULT_HORARIO_UI); }, [horarioLaboral]);

  useEffect(() => {
    setIntentList(intents);
    setCatList(categories);
    setPrioList(priorities);
    setStatusList(ticketStatuses);
    // **La gerencia entera, no las claves que esta pantalla conoce.**
    //
    // Esto ya se rompio dos veces de la misma forma. La primera armaba `{id, name}` y el campo
    // de alias salia siempre vacio **y al guardar los borraba**. Se arreglo agregando `aliases`
    // a la lista — o sea, se arreglo el sintoma. El 2026-09-02 volvio a pasar con el
    // responsable de la gerencia: Diego lo elegia, guardaba, y **desaparecia en el acto**,
    // porque el PUT lo escribia bien, la respuesta lo traia, y este efecto lo tiraba al rearmar.
    // El siguiente guardado lo habria borrado tambien de Mongo.
    //
    // La leccion no es «acordarse de sumar el campo nuevo»: es **dejar de enumerar campos**.
    // Un normalizador de pantalla que reconstruye con claves conocidas borra en silencio todo lo
    // que el producto agregue despues, y el que lo agrega no tiene forma de saber que hay una
    // lista que actualizar tres archivos mas alla. Es el mismo defecto que el repo ya arreglo en
    // `responsables_por_categoria`, en `normalize_requesting_departments`, en las subtareas de
    // plantilla y en `memoria_de_personas`.
    setDeptList((departments || []).map((d) => ({ ...d })));
  }, [intents, categories, priorities, ticketStatuses, departments]);

  return (
    <div className="space-y-4">
      <InlineHelp>
        <p className="font-semibold text-[#09090B]">Intents, Categorias, Prioridades, Estados y Gerencias</p>
        <p>Estos son los <strong>valores base</strong> que el Coordinador (primer agente) usa para clasificar cada mensaje entrante.</p>
        <p className="font-semibold text-[#09090B] mt-2">Intents (Intenciones):</p>
        <p>Representan lo que el usuario quiere. Escribi descripciones claras: <strong>cuando aplica / cuando no aplica</strong>. El coordinador usa esa descripcion literal para clasificar mensajes.</p>
        <p>Cada intent puede tener una plantilla de trabajo asociada (tab Plantillas). Sin plantilla, el ticket se crea sin plan de fases automatico.</p>
        <p className="font-semibold text-[#09090B] mt-2">Categorias:</p>
        <p>Agrupan intents tematicamente. Ej: "Infraestructura", "Aplicaciones", "Accesos". Son informativas para reportes.</p>
        <p className="font-semibold text-[#09090B] mt-2">Gerencias / sectores solicitantes:</p>
        <p>Eje organizacional tipificado (PMO / analytics). <strong>No usar etiquetas (labels) para esto.</strong> Mismo campo Mongo <code>requesting_departments</code> que en tab Trabajo.</p>
        <p className="font-semibold text-[#09090B] mt-2">Prioridades:</p>
        <p><strong>4 niveles fijos de sistema</strong> (<code>low|medium|high|critical</code>). Solo se parametriza nombre, color, SLA y descripcion. No se agregan ni borran niveles.</p>
        <p className="font-semibold text-[#09090B] mt-2">Estados de ticket:</p>
        <p>Set fijo de ids. Nombre y descripcion editables; la descripcion se inyecta al agente al coordinar.</p>
        <p className="font-semibold text-[#09090B] mt-2">Como las usa el Coordinador?</p>
        <p>El Coordinador tiene un SOUL.md que le dice como clasificar. Cuando recibe un mensaje, usa el LLM para detectar el intent y la prioridad entre las opciones definidas aqui.</p>
        <p className="mt-2 text-blue-600 font-medium">Las guias de capsula y el soul/rules del coordinador definen como se usan estos IDs en produccion.</p>
        <p className="mt-2">Listas vacías (intents/categorias) → vocabulario libre en el JSON (más flexible, menos predecible).</p>
        <p><CapsuleConfigHelpLink section="intents">Ver sección Intents del manual</CapsuleConfigHelpLink></p>
      </InlineHelp>
      {/* Horario laboral: el reloj del SLA solo corre acá adentro. Va antes de Intents
          porque es lo que le da sentido a las horas que se configuran ahí: sin esto, "4
          horas" de un pedido del viernes a la tarde vencía el sábado a la mañana. */}
      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]">
            <Clock className="w-4 h-4 inline mr-1.5" /> Horario laboral
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <p className="text-[11px] text-zinc-500">
            El reloj del SLA corre <strong>solo dentro de esta ventana</strong>. Los días que el área
            no trabaja no suman horas: un pedido del viernes a las 16 llega al lunes con 1 hora, no con 65.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              {DIAS_SEMANA.map((d) => {
                const activo = (horario.dias || []).includes(d.n);
                return (
                  <button
                    key={d.n}
                    type="button"
                    onClick={() => {
                      const dias = activo
                        ? (horario.dias || []).filter((x) => x !== d.n)
                        : [...(horario.dias || []), d.n].sort((a, b) => a - b);
                      setHorario({ ...horario, dias });
                    }}
                    className={`text-[11px] px-2 h-8 rounded-sm border ${activo
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-white text-zinc-500 border-brand-border'}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-1 text-[11px] text-zinc-500">
              de
              <Input type="time" className="rounded-sm text-xs h-8 w-28"
                value={horario.desde || '08:00'}
                onChange={(e) => setHorario({ ...horario, desde: e.target.value })} />
              a
              <Input type="time" className="rounded-sm text-xs h-8 w-28"
                value={horario.hasta || '17:00'}
                onChange={(e) => setHorario({ ...horario, hasta: e.target.value })} />
            </div>
            {/* **La pausa resta del esfuerzo, no del reloj del SLA.** De 8:30 a 17:30 hay nueve
                horas de presencia y ocho de trabajo: la que falta es el almuerzo. Lo que usa este
                numero es cuanto dura una tarea de N horas —su barra en el Gantt, y si alguien esta
                sobreasignado—; el reloj del compromiso sigue midiendo la ventana entera, que es el
                lado seguro del error. Ver el docstring de `horas_por_dia`. */}
            <div className="flex items-center gap-1 text-[11px] text-zinc-500">
              menos
              <Input type="number" min="0" step="15" className="rounded-sm text-xs h-8 w-20"
                value={horario.pausa_minutos ?? 0}
                data-testid="horario-pausa"
                onChange={(e) => setHorario({ ...horario, pausa_minutos: Number(e.target.value) || 0 })} />
              min de pausa (almuerzo)
              <span className="text-zinc-400">
                = {(() => {
                  const aMin = (h) => {
                    const [hh, mm] = String(h || '0:0').split(':');
                    return (Number(hh) || 0) * 60 + (Number(mm) || 0);
                  };
                  const bruto = aMin(horario.hasta || '17:00') - aMin(horario.desde || '08:00');
                  const neto = Math.max(0, bruto - (Number(horario.pausa_minutos) || 0));
                  return `${Math.round((neto / 60) * 100) / 100} h de esfuerzo por dia`;
                })()}
              </span>
            </div>
            <Input className="rounded-sm text-xs h-8 w-64" placeholder="Zona horaria (IANA)"
              value={horario.zona || ''}
              onChange={(e) => setHorario({ ...horario, zona: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-500">Feriados (AAAA-MM-DD, separados por coma)</Label>
            <Input className="rounded-sm text-xs h-8 mt-1" placeholder="2026-12-25, 2027-01-01"
              value={(horario.feriados || []).join(', ')}
              onChange={(e) => setHorario({
                ...horario,
                feriados: e.target.value.split(',').map((f) => f.trim()).filter(Boolean),
              })} />
          </div>
          <Button onClick={() => onSaveHorario(horario)} disabled={saving} className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs">
            <Save className="w-3 h-3 mr-1" /> Guardar horario
          </Button>

          {/* **Cuándo se considera que alguien se hizo cargo (abierto el 2026-10-05).** El motor lee
              esto desde que el reloj de atención existe, el API lo aceptaba, y **no había pantalla**:
              se podía cambiar sólo escribiendo en Mongo. Y es la parametrización que explica el
              indicador que más confunde del tablero —«Esperando ahora»—, así que una ayuda que dice
              «según tu configuración» sobre algo inalcanzable era una burla. */}
          <div className="border-t border-brand-border pt-3 mt-1">
            <Label className="text-[11px] text-zinc-500">
              Cuándo se detiene el reloj de atención
            </Label>
            <p className="text-[10px] text-zinc-500 mt-0.5 mb-1.5">
              El reloj de atención mide <b>cuánto tarda quien pidió en recibir señales de vida</b>.
              Esto decide qué cuenta como señal. Tomar el caso no cuenta en ninguna de las tres:
              es una decisión interna y quien espera no la ve.
            </p>
            <select
              className="w-full border border-brand-border rounded-sm h-8 px-1 text-xs bg-white"
              value={modoDeAtencion}
              data-testid="sla-modo-atencion"
              onChange={(e) => setModoDeAtencion(e.target.value)}
            >
              <option value="mensaje_humano">Un mensaje de una persona a quien pidió</option>
              <option value="estado">Que el caso entre en un estado que cuente como atendido</option>
              <option value="lo_primero_que_ocurra">Lo primero que ocurra de las dos</option>
            </select>
            {modoDeAtencion !== 'mensaje_humano' && (
              <p className="text-[10px] text-amber-700 mt-1">
                Con esta opción hace falta que algún estado tenga marcado{' '}
                <b>«entrar cuenta como hacerse cargo»</b> en el tab de estados; si no, el reloj no se
                detiene nunca.
              </p>
            )}
            <Button
              onClick={() => onSaveSla({ ...(sla || {}), atencion_se_detiene_con: modoDeAtencion })}
              disabled={saving}
              className="mt-2 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs"
              data-testid="guardar-sla"
            >
              <Save className="w-3 h-3 mr-1" /> Guardar el reloj de atención
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Roles del equipo */}
      <QueSeConfiguraAca
        testId="ayuda-roles-del-equipo"
        queEs="Los puestos de trabajo de esta área: quién hace qué. No son personas — las personas se asignan en Equipo."
        paraQue="Todo lo que reparte trabajo apunta a un rol y no a alguien: los intents, las plantillas, las autorizaciones. Así el reparto sigue funcionando cuando alguien se va de vacaciones, cambia de puesto o entra al equipo."
        aQueAfecta={<>A <b>dónde cae cada caso</b> y a quién le llega cada tarea. Cambiar quién ocupa un rol alcanza a todo lo que ese rol reparte, sin tocar ni un intent ni una plantilla.</>}
        ejemplo={<>Marcás «Soporte N1» como <b>entrada</b>: ahí cae lo que el clasificador no supo dirigir. Marcás «Jefatura de Sistemas» como <b>jefatura</b>: cuando alguien pide una autorización eligiendo «el jefe de esta área», le llega a quien ocupe ese rol hoy.</>}
        ojo={<>Un rol <b>sin nadie asignado en Equipo</b> se ve igual de bien acá y no resuelve a nadie: los casos que dirija van a nacer sin dueño. Y borrar un rol no reasigna lo que ya apuntaba a él.</>}
      />

      <Card className="border border-brand-border rounded-sm" data-testid="team-roles-editor">
        <CardHeader className="pb-2 border-b border-brand-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]">Roles del equipo</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setRolList([...rolList, { id: '', name: '', description: '' }])} className="rounded-sm text-xs h-7"><Plus className="w-3 h-3 mr-1" /> Rol</Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-1">
          <p className="text-[10px] text-zinc-500 mb-2">
            Quien hace que trabajo en esta capsula. Cada intent dirige a un rol, y en <strong>Equipo</strong> se
            define que personas lo tienen. Uno tiene que ser el <strong>de entrada</strong>: ahi cae lo que ningun
            intent dirige. Y uno puede ser la <strong>jefatura</strong>: es a quien se le dirige una autorizacion
            cuando se elige «el jefe de esta area», sin tener que nombrar a nadie.
          </p>
          {rolList.map((item, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <Input className="col-span-2 rounded-sm text-xs h-8" placeholder="ID" value={item.id} onChange={e => { const l = [...rolList]; l[i] = { ...item, id: e.target.value }; setRolList(l); }} />
              <Input className="col-span-3 rounded-sm text-xs h-8" placeholder="Nombre" value={item.name || ''} onChange={e => { const l = [...rolList]; l[i] = { ...item, name: e.target.value }; setRolList(l); }} />
              <Input className="col-span-4 rounded-sm text-xs h-8" placeholder="Que atiende (lo lee quien configura, no el agente)" value={item.description || ''} onChange={e => { const l = [...rolList]; l[i] = { ...item, description: e.target.value }; setRolList(l); }} />
              <label className="col-span-1 flex items-center gap-1 text-[10px] text-zinc-500" title="A este rol le llega lo que ningun intent dirige. Exactamente uno.">
                <input
                  type="radio"
                  name="rol-de-entrada"
                  checked={!!item.es_rol_de_entrada}
                  data-testid={`rol-entrada-${i}`}
                  onChange={() => setRolList(rolList.map((x, idx) => ({ ...x, es_rol_de_entrada: idx === i })))}
                />
                entrada
              </label>
              {/* **Es una casilla y no un radio como el de entrada, y la diferencia importa.**
                  Un area tiene siempre un rol de entrada —algo tiene que recibir lo que ningun
                  intent dirige— pero puede no tener jefatura declarada, y con un radio no hay
                  forma de volver atras: una vez marcado, queda marcado para siempre. Marcar uno
                  desmarca los demas igual, porque el resolvedor toma el primero. */}
              <label className="col-span-1 flex items-center gap-1 text-[10px] text-zinc-500" title="Quien manda en esta area. Es a quien se le dirige una autorizacion sin nombrar a nadie.">
                <input
                  type="checkbox"
                  checked={!!item.es_jefatura}
                  data-testid={`rol-jefatura-${i}`}
                  onChange={(e) => setRolList(rolList.map((x, idx) => ({
                    ...x, es_jefatura: e.target.checked && idx === i,
                  })))}
                />
                jefatura
              </label>
              <Button variant="ghost" size="sm" onClick={() => setRolList(rolList.filter((_, idx) => idx !== i))} className="col-span-1 h-8 text-red-400"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
          {!rolList.length && (
            <p className="text-[10px] text-zinc-400">Sin roles. Mientras no haya ninguno, el reparto por intent no tiene a donde apuntar.</p>
          )}
          <Button onClick={() => onSaveTeamRoles(rolList)} disabled={saving} className="mt-2 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs">
            <Save className="w-3 h-3 mr-1" /> Guardar Roles
          </Button>
        </CardContent>
      </Card>

      {/* Intents.

          Antes eran 23 filas de dos lineas cada una, con todo a 10px y sin separacion: para
          cambiarle el SLA a uno habia que contar columnas. Ahora cada intent es una fila que
          se lee de un vistazo —id, nombre y lo que tiene configurado— y se abre solo el que
          se quiere tocar. Con buscador, porque 23 no se recorren con la vista. */}
      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em] shrink-0">
              <Zap className="w-4 h-4 inline mr-1.5" /> Intents
            </CardTitle>
            <Input
              className="rounded-sm text-xs h-7 max-w-xs"
              placeholder="Buscar por id, nombre o criterio…"
              value={intentQ}
              onChange={(e) => setIntentQ(e.target.value)}
            />
            <Button size="sm" variant="outline" onClick={() => { setIntentList([...intentList, { id: '', name: '', description: '' }]); setIntentAbierto(intentList.length); }} className="rounded-sm text-xs h-7 shrink-0">
              <Plus className="w-3 h-3 mr-1" /> Intent
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1">
            {intentList.length} intents · {intentList.filter((x) => x.es_fallback).length} marcado como hueco
            {intentQ && ` · ${intentList.filter((x) => coincideIntent(x, intentQ)).length} coinciden`}
          </p>
        </CardHeader>
        <CardContent className="p-3 space-y-1">
          {intentList.map((item, i) => {
            if (!coincideIntent(item, intentQ)) return null;
            const tpl = templateForIntent(workflowTemplates, item.id);
            const abierto = intentAbierto === i;
            const rolNombre = (teamRoles || []).find((r) => r.id === item.rol_por_defecto)?.name;
            return (
              <div key={i} className={`border rounded-sm ${abierto ? 'border-brand-primary/40 bg-brand-primary/[0.02]' : 'border-brand-border'}`}>
                {/* Cabecera: lo que hace falta para encontrarlo, sin abrirlo. */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setIntentAbierto(abierto ? null : i)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIntentAbierto(abierto ? null : i); } }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-zinc-50 rounded-sm"
                  data-testid={`intent-fila-${i}`}
                >
                  {abierto ? <ChevronUp className="w-3 h-3 text-zinc-400 shrink-0" /> : <ChevronDown className="w-3 h-3 text-zinc-400 shrink-0" />}
                  <code className="text-[10px] font-mono text-zinc-500 shrink-0 w-52 truncate" title={item.id}>{item.id || '(nuevo)'}</code>
                  <span className="text-xs font-medium truncate flex-1">{item.name || <em className="text-zinc-400">sin nombre</em>}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {item.es_fallback && <Badge className="text-[9px] bg-amber-100 text-amber-800 hover:bg-amber-100 rounded-sm">hueco</Badge>}
                    {item.investigacion_max_rondas == null && rondasDefault != null
                      ? <Badge variant="outline" className="text-[9px] rounded-sm text-zinc-500">{rondasDefault} rondas (general)</Badge>
                      : null}
                    {item.investigacion_max_rondas === 0
                      ? <Badge variant="outline" className="text-[9px] rounded-sm text-zinc-400">sin investigar</Badge>
                      : item.investigacion_max_rondas != null && <Badge variant="outline" className="text-[9px] rounded-sm">{item.investigacion_max_rondas} rondas</Badge>}
                    {item.sla_atencion_horas != null && <Badge variant="outline" className="text-[9px] rounded-sm">SLA {item.sla_atencion_horas}h</Badge>}
                    {rolNombre && <Badge variant="outline" className="text-[9px] rounded-sm">{rolNombre}</Badge>}
                    {!tpl && <Badge variant="outline" className="text-[9px] rounded-sm text-amber-700 border-amber-300">sin plantilla</Badge>}
                  </div>
                </div>

                {abierto && (
                  <div className="border-t border-brand-border p-3 space-y-3">
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-4">
                        <Label className="text-[10px] uppercase text-zinc-500">ID</Label>
                        <Input className="rounded-sm text-xs h-8 mt-1 font-mono" placeholder="DES.ALGO" value={item.id} onChange={e => { const l = [...intentList]; l[i] = { ...item, id: e.target.value }; setIntentList(l); }} />
                      </div>
                      <div className="col-span-8">
                        <Label className="text-[10px] uppercase text-zinc-500">Nombre</Label>
                        <Input className="rounded-sm text-xs h-8 mt-1" placeholder="Como se llama para la gente" value={item.name} onChange={e => { const l = [...intentList]; l[i] = { ...item, name: e.target.value }; setIntentList(l); }} />
                      </div>
                    </div>

                    <div>
                      <Label className="text-[10px] uppercase text-zinc-500">Criterio para el coordinador</Label>
                      {/* Textarea y no input: es el texto que decide la clasificacion y suele
                          tener varias frases. En una linea no se podia leer ni corregir. */}
                      <textarea
                        className="w-full border border-brand-border rounded-sm p-2 text-xs mt-1 min-h-[72px]"
                        placeholder="Cuando aplica este intent y cuando no. Lo lee el clasificador."
                        value={item.description || ''}
                        onChange={e => { const l = [...intentList]; l[i] = { ...item, description: e.target.value }; setIntentList(l); }}
                      />
                    </div>

                    <div className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase text-zinc-500" title="Se compromete por tipo de pedido, no por urgencia.">SLA respuesta / resolución</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input type="number" min="0" className="rounded-sm text-xs h-8 w-16" placeholder="h" value={item.sla_atencion_horas ?? ''} onChange={e => { const l = [...intentList]; const v = e.target.value; l[i] = { ...item, sla_atencion_horas: v === '' ? null : parseInt(v, 10) || 0 }; setIntentList(l); }} />
                          <span className="text-[10px] text-zinc-400">/</span>
                          <Input type="number" min="0" className="rounded-sm text-xs h-8 w-16" placeholder={item.compromiso === 'planificado' ? 'al planificar' : 'sin reloj'} disabled={item.compromiso === 'planificado'} value={item.sla_resolucion_horas ?? ''} onChange={e => { const l = [...intentList]; const v = e.target.value; l[i] = { ...item, sla_resolucion_horas: v === '' ? null : parseInt(v, 10) || 0 }; setIntentList(l); }} />
                          <span className="text-[10px] text-zinc-400">h</span>
                        </div>
                        {/* **Cómo se compromete este tipo de pedido** (2026-10-09). `sla`: el plazo de
                            resolución corre desde el primer contacto —un incidente, un pedido
                            estándar—. `planificado`: no tiene plazo al entrar, y la fecha se
                            compromete al planificarlo —una mejora, un proyecto—. Al pasar a
                            planificado se vacía el plazo, porque el backend rechaza los dos juntos. */}
                        <select
                          data-testid={`intent-compromiso-${i}`}
                          className="mt-1 h-7 w-full text-[10px] border border-brand-border rounded-sm px-1 bg-white"
                          title="SLA: el plazo corre desde el primer contacto. Planificado: no tiene plazo al entrar, y la fecha se compromete al planificar el trabajo."
                          value={item.compromiso === 'planificado' ? 'planificado' : 'sla'}
                          onChange={e => { const l = [...intentList]; const modo = e.target.value; l[i] = { ...item, compromiso: modo, ...(modo === 'planificado' ? { sla_resolucion_horas: null } : {}) }; setIntentList(l); }}
                        >
                          <option value="sla">Se compromete por SLA</option>
                          <option value="planificado">Se compromete al planificar</option>
                        </select>
                      </div>

                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase text-zinc-500" title="Rondas de investigación en TODA la conversación. El tope por turno es otro y lo pone cada agente (Agentes → Runtime): manda el más chico de los dos. Una ronda es un turno del modelo, no una llamada: pedir cuatro herramientas juntas cuesta una. Vacío = esta cápsula no opina y decide el del agente. 0 = no hay nada que diagnosticar.">Investigación</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input type="number" min="0" max="12" className="rounded-sm text-xs h-8 w-14" placeholder={rondasPlaceholder} title={rondasTitulo} data-testid={`intent-rondas-${i}`} value={item.investigacion_max_rondas ?? ''} onChange={e => { const l = [...intentList]; const v = e.target.value; l[i] = { ...item, investigacion_max_rondas: v === '' ? null : parseInt(v, 10) || 0 }; setIntentList(l); }} />
                          <span className="text-[10px] text-zinc-400">rondas / conversación</span>
                        </div>
                      </div>

                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase text-zinc-500" title={topeTokensTitulo}>Techo de tokens</Label>
                        <Input type="number" min="0" step="50000" className="rounded-sm text-xs h-8 mt-1" placeholder={topeTokensPlaceholder} title={topeTokensTitulo} value={item.investigacion_max_tokens ?? ''} onChange={e => { const l = [...intentList]; const v = e.target.value; l[i] = { ...item, investigacion_max_tokens: v === '' ? null : parseInt(v, 10) || 0 }; setIntentList(l); }} />
                      </div>

                      {/* **El esfuerzo del tipo de pedido, que el motor leía y la consola no dejaba
                          tocar (abierto el 2026-10-05).** De acá sale el reparto por peso entre las
                          fases de la plantilla, y de acá sale que un caso **no** figure «esperando
                          estimación». Sólo se podía cargar escribiendo en Mongo a mano.

                          Vacío es la respuesta correcta y frecuente: los pedidos que no se pueden
                          estimar hasta analizarlos —un cambio adaptativo puede ser 8 h o 160— lo
                          dejan vacío a propósito, y su esfuerzo lo entrega la fase de análisis. */}
                      <div className="col-span-3">
                        <Label
                          className="text-[10px] uppercase text-zinc-500"
                          title="Cuánto trabajo suele llevar este tipo de pedido, en horas. Es esfuerzo, no plazo: el SLA dice cuándo hay que tenerlo, esto dice cuánto cuesta hacerlo. Se reparte entre las fases de la plantilla según el peso de cada una. Vacío = no se puede saber de antemano, y lo estima la fase de análisis."
                        >
                          Esfuerzo típico
                        </Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            type="number" min="0" step="0.5"
                            className="rounded-sm text-xs h-8 w-16"
                            placeholder="no se sabe"
                            data-testid={`intent-esfuerzo-${i}`}
                            value={item.esfuerzo_estimado_horas ?? ''}
                            onChange={e => {
                              const l = [...intentList];
                              const v = e.target.value;
                              // Cero **es** vacío: cero horas de trabajo no existe, y dejarlo pasar
                              // produciría tareas de duración cero que el Gantt dibuja como una raya.
                              const n = v === '' ? null : (parseFloat(v) || null);
                              l[i] = { ...item, esfuerzo_estimado_horas: n && n > 0 ? n : null };
                              setIntentList(l);
                            }}
                          />
                          <span className="text-[10px] text-zinc-400">h</span>
                        </div>
                      </div>

                      <div className="col-span-3">
                        <Label className="text-[10px] uppercase text-zinc-500">Rol que lo atiende</Label>
                        <select
                          className="w-full border border-brand-border rounded-sm h-8 px-1 text-xs bg-white mt-1"
                          value={item.rol_por_defecto || ''}
                          data-testid={`intent-rol-${i}`}
                          onChange={e => { const l = [...intentList]; l[i] = { ...item, rol_por_defecto: e.target.value || null }; setIntentList(l); }}
                        >
                          <option value="">(el de entrada)</option>
                          {(teamRoles || []).map((r) => (<option key={r.id} value={r.id}>{r.name || r.id}</option>))}
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-brand-border pt-2">
                      <div className="flex items-center gap-3">
                        {tpl
                          ? <Badge variant="outline" className="text-[10px] font-normal">Plantilla: {tpl.name || tpl.template_id}</Badge>
                          : <span className="text-[10px] text-amber-700">Sin plantilla de trabajo — configurala en el tab Plantillas</span>}
                        {/* El hueco tiene que tener nombre. Un intent nuevo nacia sin
                            `es_fallback` y no habia forma de marcarlo desde la pantalla: si
                            era el unico, el guardado se rechazaba con un 400 que no se veia. */}
                        <label className="text-[10px] text-zinc-500 flex items-center gap-1" title="El intent al que cae lo que el clasificador no supo clasificar. Exactamente uno por cápsula.">
                          <input type="radio" name="intent-fallback" checked={!!item.es_fallback} data-testid={`intent-fallback-${i}`} onChange={() => setIntentList(intentList.map((x, idx) => ({ ...x, es_fallback: idx === i })))} />
                          es el hueco
                        </label>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => { setIntentList(intentList.filter((_, idx) => idx !== i)); setIntentAbierto(null); }} className="h-7 text-red-400 text-xs">
                        <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <Button onClick={() => onSaveIntents(intentList)} disabled={saving} className="mt-2 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs">
            <Save className="w-3 h-3 mr-1" /> Guardar Intents
          </Button>
        </CardContent>
      </Card>

      {/* Categories */}
      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]">Categorias</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setCatList([...catList, { id: '', name: '', description: '' }])} className="rounded-sm text-xs h-7"><Plus className="w-3 h-3 mr-1" /> Categoria</Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-1">
          {catList.map((item, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <Input className="col-span-3 rounded-sm text-xs h-8" placeholder="ID" value={item.id} onChange={e => { const l = [...catList]; l[i] = { ...item, id: e.target.value }; setCatList(l); }} />
              <Input className="col-span-3 rounded-sm text-xs h-8" placeholder="Nombre" value={item.name} onChange={e => { const l = [...catList]; l[i] = { ...item, name: e.target.value }; setCatList(l); }} />
              <Input className="col-span-5 rounded-sm text-xs h-8" placeholder="Descripcion (criterio)" value={item.description || ''} onChange={e => { const l = [...catList]; l[i] = { ...item, description: e.target.value }; setCatList(l); }} />
              <Button variant="ghost" size="sm" onClick={() => setCatList(catList.filter((_, idx) => idx !== i))} className="col-span-1 h-8 text-red-400"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
          <Button onClick={() => onSaveCategories(catList)} disabled={saving} className="mt-2 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs">
            <Save className="w-3 h-3 mr-1" /> Guardar Categorias
          </Button>
        </CardContent>
      </Card>

      {/* Gerencias */}
      <Card className="border border-brand-border rounded-sm" data-testid="intents-departments-editor">
        <CardHeader className="pb-2 border-b border-brand-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]">Gerencias / sectores solicitantes</CardTitle>
            {/* La ayuda va adentro del encabezado y no arriba de la tarjeta porque esta seccion
                comparte pantalla con otras cinco: puesta afuera se lee como si explicara todo. */}
            <Button size="sm" variant="outline" onClick={() => setDeptList([...deptList, { id: '', name: '' }])} className="rounded-sm text-xs h-7"><Plus className="w-3 h-3 mr-1" /> Gerencia</Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-1">
          <QueSeConfiguraAca
            testId="ayuda-gerencias"
            queEs="Las áreas de la empresa desde donde piden las cosas, y quién autoriza por cada una."
            paraQue="Es el eje que permite contestar «cuánto trabajo genera Comercial» sin depender de cómo escribió cada uno su área. Y desde que existen las autorizaciones, es de dónde sale el gerente que tiene que dar el visto bueno."
            aQueAfecta={<>A los <b>informes por área</b>, al selector de gerencia en un caso, y a quién resuelve una autorización pedida como «el gerente de su área». Un caso ya clasificado guarda el <b>id</b>, no el nombre.</>}
            ejemplo={<>El organigrama de Microsoft dice «SISTEMAS DESARROLLO» y acá la gerencia se llama «Sistemas»: se agrega ese texto como <b>alias</b> y las dos quedan siendo la misma. El <b>responsable</b> que elijas es a quien le va a llegar la autorización de la gente de esa área.</>}
            ojo={<>El <b>id no se cambia</b>: es lo que ya tienen guardado los casos clasificados, y renombrar la gerencia sin cuidado los deja huérfanos. Una gerencia <b>sin responsable</b> no rompe nada, pero una autorización pedida como «el gerente de su área» va a nacer <b>sin autorizante</b>.</>}
          />
          <p className="text-[10px] text-zinc-500 mb-2">
            No usar etiquetas para esto. Alimenta PMO y el select Gerencia en Tickets. El
            <strong> id no se puede cambiar</strong>: es lo que guardan los tickets ya clasificados.
            El <strong>responsable</strong> es quien autoriza por la gente de esa gerencia: es a quien
            le llega una autorizacion cuando se elige «el gerente de su area».
          </p>
          {deptList.map((item, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <span className="col-span-3 text-[10px] font-mono text-zinc-400 truncate" title={item.id || '(se genera del nombre)'}>
                {item.id || '(nueva)'}
              </span>
              <Input className="col-span-3 rounded-sm text-xs h-8" placeholder="Nombre (ej. Sistemas, Comercial)" value={item.name} onChange={e => { const l = [...deptList]; l[i] = { ...item, name: e.target.value }; setDeptList(l); }} />
              {/* Los alias son como escribe el area el organigrama de M365, que casi nunca
                  coincide con el nombre lindo: "SISTEMAS DESARROLLO" -> Sistemas. Se
                  preservaban al guardar pero no habia donde verlos ni agregarlos, asi que un
                  area sin mapear no se podia arreglar desde la pantalla. */}
              <Input
                className="col-span-2 rounded-sm text-xs h-8"
                placeholder="alias del organigrama, separados por coma"
                title="Como aparece esta gerencia en el organigrama de Microsoft 365"
                value={(item.aliases || []).join(', ')}
                onChange={e => { const l = [...deptList]; l[i] = { ...item, aliases: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }; setDeptList(l); }}
              />
              {/* **Quien autoriza por la gente de esta gerencia.** Sin esto, elegir «el gerente
                  de su area» al pedir una autorizacion no resuelve a nadie: la tarea nace sin
                  autorizante y queda una linea de diagnostico que nadie mira.

                  Se elige de una lista y no se tipea un correo: un correo mal escrito se guarda
                  igual de bien que uno bueno y falla recien el dia que alguien pide una
                  autorizacion. */}
              <SelectorDePersona
                className="col-span-3"
                testId={`gerencia-responsable-${i}`}
                value={item.responsable_contact_id || ''}
                onChange={(contactId) => { const l = [...deptList]; l[i] = { ...item, responsable_contact_id: contactId || null }; setDeptList(l); }}
                sinElegir="— sin responsable —"
                placeholder="Responsable…"
              />
              <Button variant="ghost" size="sm" onClick={() => setDeptList(deptList.filter((_, idx) => idx !== i))} className="col-span-1 h-8 text-red-400"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
          <Button
            /* Se manda el objeto **entero**, con su `id` y sus `aliases`. Mandaba solo
               `{name}`, y como el normalizador deriva el id del nombre cuando no viene,
               renombrar "Logistica" a "Logistica y Distribucion" le cambiaba el id y dejaba
               huerfano todo ticket que ya la referenciaba. Los aliases se perdian igual. */
            onClick={() => onSaveDepartments?.(deptList.filter((d) => (d.name || '').trim()).map((d) => ({ ...d, name: d.name.trim() })))}
            disabled={saving}
            className="mt-2 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs"
          >
            <Save className="w-3 h-3 mr-1" /> Guardar Gerencias
          </Button>
        </CardContent>
      </Card>

      {/* Priorities — cuarteto fijo de sistema */}
      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]">Prioridades del sistema (4 niveles fijos)</CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-1">
          <p className="text-[10px] text-zinc-500 mb-2">
            El codigo y los reportes usan estos 4 ids (<code>low</code>, <code>medium</code>, <code>high</code>, <code>critical</code>).
            No se pueden agregar ni borrar. Cambiá solo el nombre que ve el usuario, color, SLA y descripcion (criterio LLM).
          </p>
          {prioList.map((item, i) => (
            <div key={item.id || i} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-2 rounded-sm border border-brand-border bg-zinc-50 px-2 h-8 flex items-center text-[11px] font-mono text-zinc-600" title="ID tecnico inmutable">
                {item.id}
              </div>
              <Input className="col-span-2 rounded-sm text-xs h-8" placeholder="Nombre visible" value={item.name} onChange={e => { const l = [...prioList]; l[i] = { ...item, name: e.target.value }; setPrioList(l); }} />
              {/* El SLA salio de la prioridad el 2026-08-14. La prioridad es la urgencia de
                  este caso; el compromiso es del tipo de pedido y se configura en Intents. */}
              <Input className="col-span-5 rounded-sm text-xs h-8" placeholder="Descripcion (criterio para coordinador)" value={item.description || ''} onChange={e => { const l = [...prioList]; l[i] = { ...item, description: e.target.value }; setPrioList(l); }} />
              {/* **Interrumpe** (2026-10-09): un caso con esta prioridad va a la franja Urgente de la
                  bandeja por prioridad, antes que todo lo planificado. No cambia ningún plazo. */}
              <label className="col-span-2 flex items-center gap-1 text-[10px] text-zinc-600" title="Un caso con esta prioridad va arriba de todo en la bandeja por prioridad, antes que lo que ordenó Dirección. No cambia su plazo.">
                <input type="checkbox" data-testid={`prioridad-interrumpe-${item.id}`} checked={!!item.interrumpe}
                  onChange={e => { const l = [...prioList]; l[i] = { ...item, interrumpe: e.target.checked }; setPrioList(l); }} />
                Interrumpe lo planificado
              </label>
              <input type="color" className="col-span-1 h-8 w-full rounded-sm" value={item.color || '#6B7280'} onChange={e => { const l = [...prioList]; l[i] = { ...item, color: e.target.value }; setPrioList(l); }} />
            </div>
          ))}
          <Button onClick={() => onSavePriorities(prioList)} disabled={saving} className="mt-2 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs">
            <Save className="w-3 h-3 mr-1" /> Guardar Prioridades
          </Button>
        </CardContent>
      </Card>

      {/* Ciclo de vida de la unidad de trabajo */}
      <Card className="border border-brand-border rounded-sm">
        <CardHeader className="pb-2 border-b border-brand-border">
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.1em]">Ciclo de vida de la unidad de trabajo</CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-1">
          <p className="text-[10px] text-zinc-500 mb-2">
            Los estados son de esta capsula. Cada uno declara que significa con banderas —cual es el inicial, cual cuenta como en curso, cual detiene el reloj— y eso es lo que miran las bandejas y el tablero, no el nombre. Desde esta pantalla se editan nombre, descripcion y color; para agregar o quitar estados y cambiar las banderas, por ahora se usa un script. Las descripciones las usa el agente al coordinar el expediente. No confundir con estados de subtarea PMO.
          </p>
          {/* **La ayuda al costado, no en un modal.** Es la doctrina de la pantalla, y acá hace
              falta más que en otros lados: son siete banderas que cambian cómo se mide el trabajo
              del área. Pasar el mouse por cada una dice qué afecta y da un ejemplo. */}
          <div className="text-[10px] text-zinc-600 bg-zinc-50 border border-brand-border rounded-sm p-2 mb-2 space-y-1">
            <p>
              <b>Qué se configura acá.</b> Los estados por los que pasa un caso en esta área, y
              qué <b>significa</b> cada uno — no por su nombre, sino por lo que declara. Por eso
              un área puede llamar «finalizado» a lo que otra llama «resuelto» y las dos medir
              igual.
            </p>
            <p>
              <b>Los botoncitos son las banderas.</b> Prendidas en azul. Cambian cómo se mide el
              cumplimiento, qué aparece en las bandejas y qué se le pide a una persona al cerrar.
              <b> Pasá el mouse por cada uno</b>: dice qué afecta y da un ejemplo.
            </p>
            <p className="text-zinc-500">
              Lo más usado: <b>resol</b> en el estado con el que cerrás — hace que al cerrar se
              pida escribir qué se hizo, y eso es lo que alimenta la memoria del área.
            </p>
          </div>

          {statusList.map((item, i) => (
            <div key={item.id || i} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-2 rounded-sm border border-brand-border bg-zinc-50 px-2 h-8 flex items-center text-[11px] font-mono text-zinc-600" title="ID tecnico inmutable">
                {item.id}
              </div>
              <Input className="col-span-2 rounded-sm text-xs h-8" placeholder="Nombre visible" value={item.name} onChange={e => { const l = [...statusList]; l[i] = { ...item, name: e.target.value }; setStatusList(l); }} />
              <Input className="col-span-6 rounded-sm text-xs h-8" placeholder="Descripcion (criterio para el agente)" value={item.description || ''} onChange={e => { const l = [...statusList]; l[i] = { ...item, description: e.target.value }; setStatusList(l); }} />
              <input type="color" className="col-span-1 h-8 w-full rounded-sm" value={item.color || '#6B7280'} onChange={e => { const l = [...statusList]; l[i] = { ...item, color: e.target.value }; setStatusList(l); }} />
              {/* **Las banderas se tocan acá.** Hasta el 2026-08-25 eran una etiqueta gris de
                  sólo lectura: se veían y no había dónde cambiarlas, así que declarar un estado
                  propio exigía tocar la base a mano. Se descubrió al agregar `exige_resolucion` y
                  no encontrar dónde prenderla — la misma primitiva sin puerta, esta vez nuestra.

                  El backend valida la coherencia al guardar (`ciclo_de_vida.validar_coherencia`):
                  exactamente un estado inicial, al menos uno que detenga el reloj. Por eso acá se
                  puede tocar libremente y el error vuelve explicado. */}
              <div className="col-span-1 flex flex-wrap gap-1 justify-center">
                {BANDERAS_DEL_ESTADO.map(([clave, corto, queEs, afecta]) => (
                  <button
                    key={clave}
                    type="button"
                    data-testid={`bandera-${item.id}-${clave}`}
                    title={`${queEs}\n\n${afecta}`}
                    onClick={() => {
                      const l = [...statusList];
                      l[i] = { ...item, [clave]: !item[clave] };
                      setStatusList(l);
                    }}
                    className={`text-[9px] px-1 rounded-sm border leading-tight ${
                      item[clave]
                        ? 'bg-brand-primary text-white border-brand-primary'
                        : 'bg-white text-zinc-400 border-brand-border hover:border-brand-primary'
                    }`}
                  >
                    {corto}
                  </button>
                ))}

                {/* **Cuánto avance implica estar en este estado (abierto el 2026-10-05).** El motor
                    lo lee desde que existe —es la tercera fuente de avance de un caso, la que cubre
                    a los que no tienen tareas ni fases— y **no estaba en el editor**, así que
                    estaba muerta: un caso abierto sin tareas salía con un guion para siempre.

                    Vacío es lo correcto por omisión: **no se inventa un 50**. Un porcentaje
                    inventado en la pantalla de quien pidió es un número sobre el que esa persona va
                    a actuar —escalar, o quedarse tranquila— y no hay nada detrás. */}
                <label
                  className="flex items-center gap-1 text-[9px] text-zinc-500 ml-1"
                  title="Qué avance mostrarle a quien pidió cuando el caso está en este estado y no tiene tareas ni fases con las que medir. Vacío = no se sabe, y se muestra un guion. Los estados cerrados ya cuentan como 100 y el inicial como 0: esto es para los del medio."
                >
                  avance
                  <Input
                    type="number" min="0" max="100" step="5"
                    className="h-6 w-14 rounded-sm text-[10px] px-1"
                    placeholder="—"
                    data-testid={`avance-sugerido-${item.id}`}
                    value={item.avance_sugerido ?? ''}
                    onChange={(e) => {
                      const l = [...statusList];
                      const v = e.target.value;
                      l[i] = {
                        ...item,
                        avance_sugerido: v === '' ? null : Math.max(0, Math.min(100, parseInt(v, 10) || 0)),
                      };
                      setStatusList(l);
                    }}
                  />
                  %
                </label>
              </div>
            </div>
          ))}
          <Button onClick={() => onSaveTicketStatuses?.(statusList)} disabled={saving} className="mt-2 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs">
            <Save className="w-3 h-3 mr-1" /> Guardar Estados
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
